import React, { useEffect, useState, useRef, useCallback } from "react";
import { MDBModal, MDBModalBody, MDBModalHeader } from "mdbreact";
import { useDispatch, useSelector } from "react-redux";
import UploadIcon from "assets/images/icons/Featured icon.png";
import IFCIMG from "assets/images/newLanding/image 245.png";
import RevitImg from "assets/images/newLanding/revit 1(1).png";
import StepDoneImg from "assets/images/icons/_Step icon base.png";
import StepInProgressImg from "assets/images/icons/Content.png";
import StepIncompleteImg from "assets/images/icons/Content1.png";
import SuccessIMG from "assets/images/icons/success.png";
import { Maybe } from "helpers/maybeFunctor";
import { isValidColor, toHexColor } from "helpers/colorFormat";
import { createBrandSettings, updateBrandSettings } from "api/brands/brandSettings";
import PromotionEntries from "components/PromotionEntries/PromotionEntries";
import BrandWizardStepper from "./BrandWizardStepper";
import { verifyAll, verifyStep, canLeaveStep, previousStep } from "./brandWizardSteps";
import SocialMediaAccounts from "pages/SocialMediaAccounts";
import { isProblemStatus, statusReasonTooltip, connectButtonFor } from "pages/SocialMediaAccounts/statusDisplay";
import Loading from "../../components/ui-components/loading";

import {
  createBrandNew as createBrand,
  createIFCBrandNew as createIFCBrand,
} from "api/brands/createBrand";
import { getMileStones } from "api/milestones/getMilestones";
import addNotification from "helpers/notify";
import { choseToggle } from "helpers/toggleIfLoading";
import { useHttp } from "hooks/useHttp";
import { addNewBrand, updateBrandData } from "../../actions/user";

import Select from "react-select";
import "../../components/CreateBrandModal/CreateModal.scss";
import { uploadFileToServer } from "components/uploadFile/uploadFile";
import { getBrandSettings } from "api/brands/brandSettings";

import { useForm } from "react-hook-form";
import { getCompanySettings } from "api/companySettings/getCompanySettings";
import { verifyBrandName } from "api/brands/verifyBrandName";
import { debounce, isError } from "lodash";
import { getBrandById } from "api/brands/getBrands";
import { editBrandPut } from "api/brands/updateBrand";
import { extractImageFromZip } from "helpers/extractImageFromZip";
import { commitBrandConnectSession, getProposedBrandFields, acceptProposedField, toggleProposedField, dismissProposedField } from "api/brands/brand";

import { hydrateSocialFromBrandSettings, clearSocialSession } from 'actions/social';
import { AUTOPILOT_FEATURES, resolveAutopilotChecked, makeAutopilotToggleHandler } from 'helpers/brandAutopilot';
import { IMAGE_FALLBACK_THEMES } from 'constants/imageFallbackThemes';

// Per-field provenance badge: shows whether the current value was set by the
// user ('manual' — the enricher won't overwrite it) or came from AI ('ai').
function FieldBadge({ source }) {
  if (source !== 'manual' && source !== 'ai') return null;
  const manual = source === 'manual';
  return (
    <span
      className="ml-1 px-1 rounded"
      style={{ fontSize: 9, fontWeight: 600, verticalAlign: 'middle', background: manual ? '#ede9fe' : '#dbeafe', color: manual ? '#6d28d9' : '#1e40af' }}
      title={manual ? 'You set this manually — the AI enricher won’t overwrite it.' : 'Filled from an AI suggestion (accepted or auto-filled). Edit it to make it Manual.'}
    >
      {manual ? 'Manual' : 'AI'}
    </span>
  );
}

function ChipListField({ label, hint, items, onChange, placeholder, source }) {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(items) ? items : [];
  const commit = () => {
    const v = String(draft || "").trim();
    if (!v) return;
    onChange([...(list || []), v]);
    setDraft("");
  };
  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="form-group mt-3 w-100">
      <label className="f-14 inter text-black font-weight-500" style={{ marginBottom: 4 }}>
        {label}<FieldBadge source={source} />
      </label>
      {hint && (
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 6 }}>
          {hint}
        </div>
      )}
      <div className="d-flex flex-wrap" style={{ gap: 6, marginBottom: 6 }}>
        {list.map((it, i) => (
          <span
            key={`${i}-${it}`}
            className="border rounded px-2 py-1"
            style={{ fontSize: 12, background: "#eef", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            {it}
            <button
              type="button"
              onClick={() => removeAt(i)}
              style={{ border: "none", background: "transparent", color: "#475569", padding: 0, lineHeight: 1, cursor: "pointer" }}
              aria-label="Remove"
              title="Remove"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="d-flex" style={{ gap: 6 }}>
        <input
          type="text"
          className="form-control"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
          }}
          placeholder={placeholder || "Add item and press Enter"}
        />
        <button
          type="button"
          className="header-btn text-white bg-bid border-bid px-3"
          style={{ fontSize: 13 }}
          onClick={commit}
        >
          Add
        </button>
      </div>
    </div>
  );
}

// Overlay the live, manually-edited form fields on top of the persisted AI
// brandProfile so the preview reflects what the owner is editing right now —
// manual values WIN over the enriched ones (mirrors the FieldBadge "Manual —
// the enricher won't overwrite it" contract). Blank/whitespace overrides fall
// through to the enriched value. Returns null only when there is neither an
// enriched profile nor any manual value to surface.
function buildEffectiveBrandProfile(brandProfile, overrides = {}) {
  const base = brandProfile && typeof brandProfile === 'object' ? brandProfile : null;
  const o = overrides || {};
  const str = (v) => (typeof v === 'string' ? v.trim() : '');

  // Manually picked primary/secondary theme colors override the AI palette.
  const manualColors = [o.primaryColor, o.secondaryColor].map(str).filter(Boolean);
  const tone = str(o.tone);
  const name = str(o.name);
  const emotionalAttachment = str(o.emotionalAttachment);

  if (!base && !manualColors.length && !tone && !name && !emotionalAttachment) {
    return null;
  }

  const merged = { ...(base || {}) };
  if (manualColors.length) merged.colors = manualColors;
  if (tone) merged.tone = tone;
  if (name) merged.brandName = name;
  if (emotionalAttachment) merged.emotionalAttachment = emotionalAttachment;
  return merged;
}

function BrandProfilePreview({ brandSettings, useBrandProfile, overrides }) {
  const profile = buildEffectiveBrandProfile(brandSettings?.brandProfile || null, overrides);
  const byPlatform = brandSettings?.byPlatform || {};
  const modelImageUrls = Array.isArray(brandSettings?.modelImages)
    ? brandSettings.modelImages.filter((x) => typeof x === 'string' && x.length > 0)
    : [];

  const accounts = Object.entries(byPlatform).flatMap(([plat, bp]) =>
    (bp?.accounts || []).map((a) => ({
      platform: plat,
      name: a.accountName || a.title || plat,
      handle: a.socialHandle || null,
      avatar: a.avatar || null,
      // Token health — so the summary reflects accounts that need a reconnect
      // (worker flips status→'failed' + records statusReason on a dead token).
      status: a.status || 'connected',
      statusReason: a.statusReason || null,
    }))
  );

  const renderList = (label, items) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="mb-2">
        <div className="f-12 text-muted text-uppercase">{label}</div>
        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
          {items.map((s, i) => (
            <span key={i} className="border rounded px-2 py-1" style={{ fontSize: 12, background: '#f8f9fa' }}>{s}</span>
          ))}
        </div>
      </div>
    );
  };

  const renderText = (label, text) => {
    if (!text) return null;
    return (
      <div className="mb-2">
        <div className="f-12 text-muted text-uppercase">{label}</div>
        <div style={{ fontSize: 13 }}>{text}</div>
      </div>
    );
  };

  return (
    <div className="border rounded p-3 mt-2 mb-3" style={{ background: '#fafbff' }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong style={{ fontSize: 14 }}>
          AI brand profile {useBrandProfile ? '(in use)' : '(disabled — using your fields)'}
        </strong>
        {profile?.enrichedAt && (
          <span className="text-muted" style={{ fontSize: 12 }}>
            Enriched {new Date(profile.enrichedAt).toLocaleString()}
            {typeof profile.confidence === 'number'
              ? ` · confidence ${Math.round(profile.confidence * 100)}%`
              : ''}
          </span>
        )}
      </div>

      {!profile && (
        <div className="text-muted" style={{ fontSize: 13 }}>
          Not enriched yet. Connect at least one social account (or website) and save; the worker will synthesize a profile shortly.
        </div>
      )}

      {profile?.lastEnrichmentError && (
        <div className="text-danger mb-2" style={{ fontSize: 12 }}>
          Last enrichment error: {profile.lastEnrichmentError}
        </div>
      )}

      {profile && (
        <>
          {renderText('Brand name', profile.brandName)}
          {renderText('Big idea', profile.bigIdea)}
          {renderText('Summary', profile.summary)}
          {renderText('Tone', profile.tone)}
          {renderText('Emotional buy-in', profile.emotionalAttachment)}
          {renderText('Style', profile.style)}
          {renderText('Domain', profile.domain)}
          {renderText('Positioning', profile.positioningStatement)}
          {renderText('Voice guidelines', profile.voiceGuidelines)}
          {renderList('Target customers', profile.targetCustomers)}
          {renderList('Subjects to post about', profile.subjectsToPostAbout)}
          {renderList('Value props', profile.valueProps)}
          {renderList('Differentiators', profile.differentiators)}
          {renderList('Content pillars', profile.contentPillars)}
          {renderList('Preferred formats', profile.format)}
          {renderList('Language patterns', profile.languagePatterns)}
          {renderList('Avoid', profile.avoidList)}
          {Array.isArray(profile.colors) && profile.colors.length > 0 && (
            <div className="mb-2">
              <div className="f-12 text-muted text-uppercase">Palette</div>
              <div className="d-flex" style={{ gap: 6 }}>
                {profile.colors.map((c, i) => (
                  <span key={i} title={c} style={{
                    width: 24, height: 24, borderRadius: 4,
                    border: '1px solid #ccc', background: c,
                    display: 'inline-block'
                  }} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {modelImageUrls.length > 0 && (
        <div className="mt-3">
          <div className="f-12 text-muted text-uppercase mb-1">
            Reference images <span className="text-muted" style={{ textTransform: 'none' }}>(top-engagement, used as style references)</span>
          </div>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {modelImageUrls.slice(0, 8).map((url, i) => (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noreferrer"
                title="Open original"
                style={{ display: 'inline-block', textDecoration: 'none' }}
              >
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.opacity = 0.3; e.currentTarget.title = 'Failed to load'; }}
                  style={{
                    width: 120, height: 120, objectFit: 'cover', borderRadius: 8,
                    border: '1px solid #d4d4d4',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    background: '#f4f4f4',
                  }}
                />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3">
        <div className="f-12 text-muted text-uppercase mb-1">Connected accounts</div>
        {accounts.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 12 }}>No social accounts connected yet.</div>
        ) : (
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {accounts.map((a, i) => {
              const problem = isProblemStatus(a.status);
              const tip = statusReasonTooltip(a.status, a.statusReason);
              return (
              <div key={i} className="d-flex align-items-center border rounded px-2 py-1"
                style={{ gap: 6, background: '#fff', borderColor: problem ? '#f0a4a4' : undefined }}
                title={problem ? tip : (a.status === 'connected' ? 'Connected' : a.status)}>
                {a.avatar
                  ? <img src={a.avatar} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                  : <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#ddd', display: 'inline-block' }} />}
                <span style={{ fontSize: 12 }}>
                  <strong>{a.name}</strong> <span className="text-muted">{a.handle ? `@${a.handle}` : a.platform}</span>
                </span>
                {/* token-health dot: green=connected, red=failed/expired, amber=other */}
                <span style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                  background: a.status === 'connected' ? '#22c55e' : (problem ? '#ef4444' : '#f59e0b') }} />
                {problem && (
                  <button type="button"
                    onClick={() => { const el = document.getElementById('brand-social-accounts-panel'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    className="btn btn-sm p-0 px-1"
                    style={{ fontSize: 11, color: '#b45309', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4, lineHeight: 1.4 }}
                    title={tip || 'This account needs to be reconnected'}>
                    ⚠ {connectButtonFor(a.status).label}
                  </button>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {profile?.topPostsByPlatform && profile.topPostsByPlatform.length > 0 && (
        <div className="mt-3">
          <div className="f-12 text-muted text-uppercase mb-1">Top posts by engagement</div>
          <div className="d-flex flex-column" style={{ gap: 8, fontSize: 12 }}>
            {profile.topPostsByPlatform.slice(0, 10).map((p, i) => {
              const thumb = p.thumbnailUrl || (p.mediaType === 'photo' ? p.mediaUrl : null);
              return (
                <div key={i} className="d-flex" style={{ gap: 12 }}>
                  {thumb ? (
                    <a href={p.url} target="_blank" rel="noreferrer" style={{ flex: '0 0 auto' }}>
                      <img
                        src={thumb}
                        alt=""
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.opacity = 0.3; e.currentTarget.title = 'Failed to load'; }}
                        style={{
                          width: 110, height: 110, objectFit: 'cover', borderRadius: 8,
                          border: '1px solid #d4d4d4',
                          background: '#f4f4f4',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        }}
                      />
                    </a>
                  ) : (
                    <span
                      data-testid="post-media-placeholder"
                      style={{
                        flex: '0 0 auto', width: 110, height: 110, borderRadius: 8,
                        background: '#f0f0f0', border: '1px solid #d4d4d4',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 28, color: '#a3a3a3',
                      }}
                    >📷</span>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <a href={p.url} target="_blank" rel="noreferrer">[{p.platform}]</a>
                    {p.mediaType && p.mediaType !== 'photo' && (
                      <span className="text-muted"> · {p.mediaType}</span>
                    )}{' '}
                    {p.text ? p.text.slice(0, 140) : '(no text)'}
                    {p.engagement && (
                      <div className="text-muted">
                        {p.engagement.likes ?? 0} likes · {p.engagement.comments ?? 0} comments
                        {typeof p.engagement.shares === 'number' ? ` · ${p.engagement.shares} shares` : ''}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export { BrandProfilePreview, buildEffectiveBrandProfile };

// One-sentence summary of what THIS brand's audience most wants, shown at the
// very top of the brand-settings wizard (just after the "Edit brand" header).
// It is the highest-heuristic entry of the audience-desire dictionary, written
// by the worker's audience-intent flow from the brand's email replies, chat
// tickets, and social engagement. Renders nothing until a desire exists.
function AudienceDesireBanner({ brandSettings }) {
  const ad = brandSettings && brandSettings.audienceDesire;
  const sentence = ad && typeof ad.topDesire === 'string' ? ad.topDesire.trim() : '';
  if (!sentence) return null;
  const count = ad && typeof ad.topHeuristic === 'number' ? ad.topHeuristic : 0;
  return (
    <div
      data-testid="audience-desire-banner"
      className="d-flex align-items-start rounded p-3 mb-3"
      style={{ background: '#eef6ff', border: '1px solid #cdddff', gap: 10 }}
    >
      <span aria-hidden="true" style={{ fontSize: 18, lineHeight: '20px' }}>🎯</span>
      <div>
        <div className="f-12 text-uppercase" style={{ color: '#0F62FE', fontWeight: 600, letterSpacing: 0.4 }}>
          What your audience wants most
        </div>
        <div style={{ fontSize: 14, color: '#15172b', marginTop: 2 }}>{sentence}</div>
        {count > 1 && (
          <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
            Seen across {count} audience signals (email replies, chat, and social engagement).
          </div>
        )}
      </div>
    </div>
  );
}

export { AudienceDesireBanner };

// Thumbnail preview for a brand image supplied as a URL — used by the
// "Brand logo / watermark URL" and "Logo overlay URL" fields. These hold a
// pasted/AI-enriched URL string (not an uploaded File like "Brand image"),
// so the preview points <img> straight at the URL. It mirrors the look of
// the Brand image field's `uploaded-icon` thumbnail: until a usable image
// URL is present — or if the URL fails to load — it falls back to the same
// neutral UploadIcon placeholder so the layout never shows a broken-image
// glyph.
function LogoUrlPreview({ url, alt = "logo preview", testId }) {
  const trimmed = typeof url === "string" ? url.trim() : "";
  // Only treat absolute http(s), root-relative, or inline data-image URLs as
  // previewable; anything else (a half-typed string) stays a placeholder.
  const isPreviewable = /^(https?:\/\/|\/|data:image\/)/i.test(trimmed);
  const [errored, setErrored] = useState(false);

  // Reset the load-error flag whenever the URL changes so a corrected URL
  // gets a fresh chance to render.
  useEffect(() => {
    setErrored(false);
  }, [trimmed]);

  if (!trimmed || !isPreviewable || errored) {
    return (
      <img
        src={UploadIcon}
        className="uploaded-icon"
        alt={alt}
        data-testid={testId ? `${testId}-placeholder` : undefined}
      />
    );
  }

  return (
    <img
      src={trimmed}
      className="uploaded-icon"
      alt={alt}
      data-testid={testId}
      onError={() => setErrored(true)}
    />
  );
}

export { LogoUrlPreview };

// A brandConnectCtx left in sessionStorage by an ABANDONED wizard session
// points at that session's server-side draft brand — which may already
// carry connected social accounts and an AI-enrichment profile. If a new
// create-brand wizard picks it up, the proposal poll prefills the form
// with the previous brand's data AND the final commit lands on the old
// draft (inheriting its social connections). Purge it when the wizard
// mounts in create mode so every new brand starts from scratch; the ctx
// is (re)written by startConnect the moment the user clicks Connect, and
// mid-session consumers (commit, SMC popup) are unaffected because the
// wizard never remounts while it's open — OAuth runs in a popup window.
function purgeStaleConnectCtx(isEditMode) {
  if (isEditMode) return;
  try { sessionStorage.removeItem('brandConnectCtx'); } catch { /* unavailable */ }
}

export { purgeStaleConnectCtx };

const actionTypeOptions = [
  {label: 'Buy Product', value: 'Buy Product'},
  {label: 'Subscribe to Newsletters', value: 'Subscribe to Newsletters'},
  {label: 'Visit Website', value: 'Visit Website'},
  {label: 'Download Product', value: 'Download Product'},
];

const CreateModal = (props) => {

  const {
    img,
    favourite = false,
    date,
    brand = "",
    _id = "",
    brandSettingsId = "",
    posts = [],
    ideas = [],
    user,
    zipInfo,
    isParent = false,
    parentBrandID = "",
    milestone,
  } = props ?? {};

  console.log("CreateModal");

  // Lazy useState initializer = runs synchronously ONCE at mount, BEFORE
  // the render-body sessionStorage fallback read below computes
  // modalBrandId — so a stale draft from an abandoned session can never
  // leak into this wizard instance (see purgeStaleConnectCtx).
  useState(() => { purgeStaleConnectCtx(Boolean(props?.brand)); return null; });

  var milestoneOptionsVar = [];
  const {
    register,
    formState: { errors },
    trigger,
    reset,
    setError,
    setValue: setRHFValue,
  } = useForm({ mode: "onChange" });
  const dispatch = useDispatch();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const brandImgInputRef = useRef(null);
  const modelImgsInputRef = useRef(null);
  const ifcFileInputRef = useRef(null);

  const usersRedux = useSelector((s) => s.user.users);
  const emailRedux = useSelector((s) => s.user.userInfo.email);
  const brandsRedux = useSelector((s) => s.user.brands);

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [description, setDescription] = useState("");
  const [globalMessage, setGlobalMessage] = useState("");
  const [brandPostingInstructions, setBrandPostingInstructions] = useState("");
  const [logoUrl, setLogoUrl] = useState(""); // brand logo / watermark image URL
  // The look forced on the LAST-RESORT screenshot fallback (every image provider down →
  // the pipeline screenshots a keyword-matched stock search instead). Empty = subject only.
  const [imageFallbackTheme, setImageFallbackTheme] = useState("");
  const [overlayLogoUrl, setOverlayLogoUrl] = useState(""); // dedicated logo OVERLAY image path/url
  const [brandImage, setBrandImage] = useState(null);
  const [modelImages, setModelImages] = useState([]);
  const [brandNumber, setBrandNumber] = useState("");

  const [ifcFile, setIfcFile] = useState(null);
  const [ifcUrl, setIfcUrl] = useState("");

  const [supervisor, setSupervisor] = useState({
    label: userInfoRedux?.name,
    value: userInfoRedux?._id,
  });
  const [supervisors, setSupervisors] = useState([]);
  const [users, setUsers] = useState([]);
  const projectsRedux = useSelector((s) => s.user.brands);
  // Defaults to TRUE — autopost is the whole point of the product;
  // a brand with no platforms can opt out by unchecking the toggle.
  // Previously defaulted to false, which meant every brand the user
  // created via the wizard was silently skipped by the dailyContent
  // scheduler until they hunted down the toggle.
  const [isAuto, setIsAuto] = useState(true);
  // Informational supervisor emails (post scheduled / published / failed,
  // engagement refreshed, enrichment done, etc.). Default ON; the worker
  // silently no-ops when there's no supervisor email selected.
  const [notifySupervisorOnWorkflows, setNotifySupervisorOnWorkflows] = useState(true);
  // "Produce content automatically" — split out of autopost. When off the
  // content schedulers skip this brand entirely. Default ON.
  const [autoGenerate, setAutoGenerate] = useState(true);
  // Per-feature autopilot map (post/email/chat/answer/article/crm/sequence/
  // review/community/enrich). Only holds keys explicitly set/loaded; the
  // Step 03 Settings toggles display the effective value for unset keys via
  // resolveAutopilotChecked. The FULL merged map is sent on Save.
  const [autopilotFlags, setAutopilotFlags] = useState({});
  // Self-provisioned newsletter flow (worker newsletterProvisioner). Default OFF.
  const [autoNewsletter, setAutoNewsletter] = useState(false);
  const [autoNewsletterFrequency, setAutoNewsletterFrequency] = useState("weekly");
  const [isSupervisorToggle, setIsSupervisorToggle] = useState(false);
  const [isSupervisorsToggle, setIsSupervisorsToggle] = useState(false);
  const [contentType, setContentType] = useState("text");
  const [language, setLanguage] = useState("english");
  const [brandNumberLoading, setBrandNumberLoading] = useState(false);
  // EDIT MODE: brandSettings is derived live from state.user.brands so
  // every dispatch (UPDATE_BRAND_SETTINGS_SELECTION,
  // SET_BRAND_DEFAULT_TARGET, REMOVE_BRAND_SOCIAL_ACCOUNT, the
  // refresh() → PATCH_BRAND_SETTINGS pipeline, etc.) propagates into
  // SMA's bsFromProps without going through a setBrandSettings call.
  // CREATE MODE: no committed brand exists in Redux, so we keep a
  // local state fallback that the OAuth applyConnectPayload / wizard
  // form populates.
  const [brandSettingsLocal, setBrandSettingsLocal] = useState(null);
  const brandSettingsFromRedux = useSelector((s) => {
    if (!props.brand) return null;
    const list = Array.isArray(s.user && s.user.brands) ? s.user.brands : [];
    const b = list.find((x) => String(x && x._id) === String(props.brand));
    return (b && b.brandSettings) || null;
  });
  const brandSettings = brandSettingsFromRedux || brandSettingsLocal;
  // Back-compat shim — existing call sites still call setBrandSettings.
  // It now writes to the local fallback; in edit mode the Redux value
  // wins via the selector above anyway.
  const setBrandSettings = setBrandSettingsLocal;
  const [useBrandProfile, setUseBrandProfile] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Owner-supplied multi-entry knowledge fields. Each is a flat string[]
  // surfaced as chip-list inputs in Step 02 (Brand Info).
  const [pricingInfo, setPricingInfo] = useState([]);
  const [technicalFaq, setTechnicalFaq] = useState([]);
  const [tutorialsAndTrainings, setTutorialsAndTrainings] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  // Voice fields. Used to live AI-only on settings.brandProfile; now
  // exposed as editable inputs so the user can read & tweak what
  // enrichment proposed. The enricher force-fills these when empty,
  // so a fresh brand starts populated rather than blank.
  const [brandVoice, setBrandVoice] = useState("");
  // Reference correction of meaning — the brand canon everything must
  // harmonize with (BrandSettings.contentReferences / contentRules).
  const [contentAlignment, setContentAlignment] = useState("");
  const [contentRulesText, setContentRulesText] = useState("");
  const [brandKeywords, setBrandKeywords] = useState([]);
  const [brandHashtags, setBrandHashtags] = useState([]);
  const [enriching, setEnriching] = useState(false);

  const contentTypeOptions = [
    { value: "text", label: "Text" },
    { value: "image", label: "Image" },
    { value: "text_image", label: "Text + Image" },
    // Permissive: attach any of text / image / video and each platform takes
    // the richest it can use (video > image > text). A platform that can use
    // none of what is attached is skipped, not failed — which is how one post
    // serves YouTube (video only), Pinterest (needs media) and X (text) at once.
    { value: "text_image_video", label: "Text + Image + Video (any combination)" },
    { value: "video", label: "Video" },
    { value: "text_video", label: "Text + Video" },
    { value: "document", label: "Document", isDisabled: true },
  ];

  const languageOptions = [
    { value: "english", label: "English" },
    { value: "spanish", label: "Español" },
    { value: "french", label: "Français" },
    { value: "german", label: "Deutsch" },
    { value: "italian", label: "Italiano" },
    { value: "portuguese", label: "Português" },
    { value: "dutch", label: "Nederlands" },
    { value: "polish", label: "Polski" },
    { value: "russian", label: "Русский" },
    { value: "japanese", label: "日本語" },
    { value: "korean", label: "한국어" },
    { value: "chinese", label: "中文" },
    { value: "arabic", label: "العربية" },
    { value: "hindi", label: "हिन्दी" },
    { value: "turkish", label: "Türkçe" },
    { value: "indonesian", label: "Bahasa Indonesia" },
    { value: "vietnamese", label: "Tiếng Việt" },
    { value: "thai", label: "ไทย" },
  ];

  const Filtersupervisors = Maybe.of(usersRedux).map((users) => {
    return users;
  });

  const supervisorsOptions = Filtersupervisors?.value?.map((i) => ({
    label: i.name,
    value: i._id,
  }));

  const parentBrandOptions = brandsRedux?.map((i) => ({
    label: i.name,
    value: i._id,
  }));
  const [parentBrand, setParentBrand] = useState("");

  const PROJ_TYPES = {
    REVIT: "REVIT",
    IFC: "IFC",
  };

  const STEP = {
    STEP_01: "STEP_01", // Social Accounts
    STEP_02: "STEP_02", // Brand Info (Moved from Step_04)
    // Brand Settings — also hosts every auto/autopilot flag that used to
    // live in the standalone Step 05 (merged in; Step 05 removed).
    STEP_03: "STEP_03", // Brand Settings + Settings (auto/autopilot flags)
    STEP_04: "STEP_04", // Content Generation (saves the brand)
    // STEP_05 was retired when the old "Settings" step merged into 03. The slot
    // is reused here for PROMOTION — feature entries that become 4/day
    // value posts carrying a recorded product clip. It runs AFTER the save
    // because it writes onto a brand that already exists.
    STEP_05: "STEP_05", // Promotion (feature entries)
    STEP_06: "STEP_06", // Success Message
  };

  const primaryColorPickerRef = useRef(null);
  const secondaryColorPickerRef = useRef(null);

  const [themeError, setThemeError] = useState(false);

  const [domain, setDomain] = useState("");
  const [actionType, setActionType] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [domainError, setDomainError] = useState(false);

  const [offer, setOffer] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [emotionalAttachment, setEmotional] = useState("");

  const [website, setWebsite] = useState("");
  const [actionTypeError, setActionTypeError] = useState(false);
  const [actionUrlError, setActionUrlError] = useState(false);
  const [websiteError, setWebsiteError] = useState(false);
  const [brandPostingCadence, setBrandPostingCadence] = useState("");
  const [cadenceError, setCadenceError] = useState(false);
  const [modelType, setModelType] = useState("");
  const [postingTime, setPostingTime] = useState("");
  const [selectedBrandType, setSelectedBrandType] = useState(PROJ_TYPES.REVIT);
  const [selectedSteps, setSelectedSteps] = useState(STEP.STEP_01);
  const [milestones, setMilestones] = useState([]);
  const [milestonesOptions, setMilestonesOptions] = useState([]);

  const [includeMiscellaneous, setIncludeMiscellaneous] = useState(false);
  const [include2DElements, setInclude2DElements] = useState(false);
  const [brandData, setBrandData] = useState({});
  const createObjectForm = {};

  const [themeName, setThemeName] = useState('#000000');
  const [secondaryTheme, setSecondaryTheme] = useState('#ffffff');

  useEffect(() => {
    // reserved
  }, []);

  // on Click close reset form
  const toggle = () => {
    // clear any in-progress social draft/cache before closing
    clearSocialDraftsAndCaches();

    // NEW: also clear session-level social connections
    dispatch(clearSocialSession());

    props.toggle();
    props.setSelectedBrand(null);
    setBrandSettings(null);
    setName("");
    setDescription("");
    setThemeName("");
    setSecondaryTheme("");
    setBrandPostingInstructions("");
    setLogoUrl("");
    setOverlayLogoUrl("");
    setGlobalMessage("");
    setBrandPostingCadence("");
    setLanguage("");
    setContentType("");
    setDomain("");
    setWebsite("");
    setActionType("");
    setActionUrl("");
    setBrandImage(null);
    setModelImages([]);
    setIfcFile(null);
    setSupervisors([]);
    setUsers([]);
    setIsAuto(true);
    setNotifySupervisorOnWorkflows(true);
    setAutoGenerate(true);
    setAutopilotFlags({});
    setAutoNewsletter(false);
    setAutoNewsletterFrequency("weekly");
    setParentBrand("");
    setSelectedBrandType(PROJ_TYPES.REVIT);
    setSelectedSteps(STEP.STEP_01);
    setUseBrandProfile(true);
    setPreviewOpen(false);
    setPricingInfo([]);
    setTechnicalFaq([]);
    setTutorialsAndTrainings([]);
    setCompetitors([]);
    setBrandVoice("");
    setBrandKeywords([]);
    setBrandHashtags([]);

    // Clear social “in-progress” caches when the wizard closes
    dispatch({ type: 'SOCIAL_DRAFT_CLEAR', brandSettingsId: 'new' });
    try {
      sessionStorage.removeItem('social:last');
      sessionStorage.removeItem('brandConnectCtx');
      Object.keys(localStorage)
        .filter(k => k.startsWith('social:temp:'))
        .forEach(k => localStorage.removeItem(k));
    } catch {}

    reset();
  };

  // Prevent setState on unmounted component
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Clear ephemeral social connect caches + "new" draft
  const clearSocialDraftsAndCaches = useCallback(() => {
    try {
      dispatch({ type: 'SOCIAL_DRAFT_CLEAR', brandSettingsId: 'new' });
    } catch {}
    try { sessionStorage.removeItem('social:last'); } catch {}
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('social:temp:')) localStorage.removeItem(k);
      });
    } catch {}
  }, [dispatch]);

  useEffect(() => {
    if (props.isOpen) {
      clearSocialDraftsAndCaches();
    }
  }, [props.isOpen, clearSocialDraftsAndCaches]);

  /** ---------- NEW: session hydrate/clear wiring ---------- */
  // Compute modal-scope IDs: prefer explicit props, then loaded state,
  // then the draft brand ID startConnect stashed in sessionStorage.
  // The draft fallback is critical for create mode — without it,
  // modalBrandId stays empty until the user clicks Save, so the
  // proposal poll never fires during the wizard and the auto-name
  // / auto-field-fill UX doesn't happen.
  let _draftBrandIdFromSession = '';
  let _draftBrandSettingsIdFromSession = '';
  try {
    const _ctx = JSON.parse(sessionStorage.getItem('brandConnectCtx') || 'null');
    if (_ctx && _ctx.brandId) _draftBrandIdFromSession = String(_ctx.brandId);
    if (_ctx && _ctx.brandSettingsId) _draftBrandSettingsIdFromSession = String(_ctx.brandSettingsId);
  } catch (_) { /* sessionStorage may be unavailable */ }
  const modalBrandId = _id || brand || _draftBrandIdFromSession || "";
  const modalBrandSettingsId = brandSettingsId || (brandSettings && brandSettings._id) || _draftBrandSettingsIdFromSession || "";

  // Promotion entries (Step 05). Kept local until the brand is saved, then
  // written with a normal settings PUT — `promotion` is a nested object, and
  // helpers/brandSettingsFields merges it per subfield, so sending only these
  // keys cannot clobber the rest of the brand's settings.
  const [promotion, setPromotion] = useState({ features: [], ctaText: "", ctaUrl: "" });
  const [savingPromotion, setSavingPromotion] = useState(false);
  const savePromotion = async () => {
    const id = modalBrandSettingsId;
    // No id means the save did not return one; losing the entries silently
    // would be worse than saying so.
    if (!id) { addNotification("Brand settings not ready — promotion not saved.", "danger"); return; }
    // Verify before writing. A promotion CTA that isn't a URL becomes a dead
    // link on 4 posts a day, and an untitled feature can never be posted at
    // all — both are cheap to catch here and expensive to notice later.
    const problems = verifyStep(STEP.STEP_05, { promotion });
    if (problems.length) { addNotification(problems[0].message, "danger"); return; }
    setSavingPromotion(true);
    try {
      await updateBrandSettings({ _id: id, promotion });
      addNotification("Promotion saved.", "success");
      setSelectedSteps(STEP.STEP_06);
    } catch (e) {
      addNotification("Could not save promotion — your entries are still here.", "danger");
    } finally {
      setSavingPromotion(false);
    }
  };

  // Everything the step verification looks at, in one place. Read from the
  // live form state rather than the saved brand so the flags react as the
  // owner types — the point is to catch a bad value before it is saved, not
  // after.
  const wizardValues = {
    name,
    website,
    domain,
    theme: themeName,
    secondaryTheme,
    actionUrl,
    promotion,
  };
  const stepProblems = verifyAll(wizardValues);

  // Jumping between steps from the trail at the top.
  //
  // Going BACK is always allowed — trapping someone on a step because of a
  // typo they may need to look up is how a wizard becomes a maze, and the
  // "Verification needed" flag stays on the trail either way. Going FORWARD
  // is what verification gates: an EMPTY field is fine (enrichment fills it,
  // and the setup-needed email covers the rest), but a value that nothing
  // downstream can use — a colour that isn't a colour, a URL that isn't a URL
  // — would otherwise be discovered days later as a dead link in a live post.
  const goToStep = (target) => {
    if (target === selectedSteps) return;
    const { ok, problems } = canLeaveStep(selectedSteps, target, wizardValues);
    if (!ok) {
      addNotification(problems[0].message, "danger");
      return;
    }
    setSelectedSteps(target);
  };

  useEffect(() => {
    if (!props.isOpen) return;

    if (modalBrandId && modalBrandSettingsId) {
      // Existing brand case → mirror byPlatform into session connections
      dispatch(hydrateSocialFromBrandSettings(modalBrandId, modalBrandSettingsId));
    } else {
      // Create-mode / missing IDs → clear session connections
      dispatch(clearSocialSession());
    }
  }, [props.isOpen, modalBrandId, modalBrandSettingsId, dispatch]);
  /** ------------------------------------------------------ */

  // AI-enrichment: when the modal opens on an existing brand, fetch the
  // enrichment proposal and prefill ANY empty form field with it. Fields
  // the user has already typed into are left alone — that policy is the
  // whole point of returning a proposal instead of patching server-side.
  // Re-fetched on demand by the Re-enrich button via `refreshProposal`.
  const [aiPrefilledFields, setAiPrefilledFields] = useState([]);
  // Per-field "AI suggest off" toggle map mirrored from backend.
  // Server-side this lives at BrandSettings.disabledProposals and is
  // surfaced on every /proposed-fields response. Local state is the
  // optimistic cache so toggle clicks flip instantly.
  const [disabledProposals, setDisabledProposals] = useState({});
  // Per-field provenance ('manual' | 'ai') from /proposed-fields — drives the
  // per-field badge so the user sees which fields they set vs the AI did.
  const [fieldSources, setFieldSources] = useState({});
  // For fields the user already typed into where the AI proposes a
  // DIFFERENT value, we don't overwrite — we record the diff so the
  // review panel can offer a per-field "Apply" button.
  const [aiProposalDiffs, setAiProposalDiffs] = useState({});
  const [proposalEnrichedAt, setProposalEnrichedAt] = useState(null);
  const [showProposalPanel, setShowProposalPanel] = useState(true);
  const prefilledOnceRef = useRef(false);
  // The brand a proposed-fields fetch belongs to — drops a fetch that resolves
  // AFTER the user switched brands so its suggestions can't leak in.
  const proposalBrandRef = useRef("");

  // Field labels for the review panel — keep human-readable.
  const FIELD_LABELS = {
    description: 'Brand description',
    offer: 'Offer / value prop',
    painPoint: 'Customer pain point',
    emotionalAttachment: 'Emotional attachment',
    brandDomain: 'Brand domain / industry',
    globalBrandMessageForAudience: 'Global brand message',
    brandPostingInstructions: 'Posting instructions',
    brandThemePrimaryColor: 'Primary theme color',
    brandThemeSecondaryColor: 'Secondary theme color',
    pricingInfo: 'Pricing / plans',
    technicalFaq: 'Technical FAQ',
    tutorialsAndTrainings: 'Tutorials & trainings',
    competitors: 'Competitors',
    modelImages: 'Reference images',
    brandVoice: 'Brand voice',
    brandKeywords: 'Brand keywords',
    brandHashtags: 'Brand hashtags',
  };

  const applyProposal = useCallback((proposalRes) => {
    if (!proposalRes || !proposalRes.proposal) return;
    const p = proposalRes.proposal;
    const filled = [];
    const diffs = {};
    // Helper: only set a field if its current value is empty/default.
    // Otherwise, if the user's value differs from the AI's, record it
    // as a diff for the review panel to surface.
    const fillStr = (current, setter, fieldName) => {
      const next = p[fieldName];
      const hasNext = typeof next === 'string' && next.trim().length > 0;
      const hasCurrent = current && String(current).trim().length > 0;
      if (!hasNext) return;
      // Honor user's Apply: once they accepted this exact proposal,
      // stop re-surfacing it. Only re-surface if a future enrichment
      // proposes a DIFFERENT value.
      if (acceptedProposalsRef.current[fieldName] === next.trim() ||
          acceptedProposalsRef.current[fieldName] === next) {
        return;
      }
      if (!hasCurrent) {
        setter(next);
        filled.push(fieldName);
      } else if (String(current).trim() !== next.trim()) {
        diffs[fieldName] = { current: String(current), proposed: next, kind: 'string', setter };
      }
    };
    const fillArr = (current, setter, fieldName) => {
      const next = p[fieldName];
      const hasNext = Array.isArray(next) && next.length > 0;
      const hasCurrent = Array.isArray(current) && current.length > 0;
      if (!hasNext) return;
      const accepted = acceptedProposalsRef.current[fieldName];
      if (Array.isArray(accepted) && JSON.stringify(accepted) === JSON.stringify(next)) {
        return;
      }
      if (!hasCurrent) {
        setter(next);
        filled.push(fieldName);
      } else {
        const a = JSON.stringify(current); const b = JSON.stringify(next);
        if (a !== b) diffs[fieldName] = { current, proposed: next, kind: 'array', setter };
      }
    };

    // BRAND NAME is the highest-priority enrichment target — a brand
    // created with "Brand-mfx7vh3a" placeholder should flip to the
    // real name the moment enrichment proposes one. fillStr's
    // placeholder-detector also treats Brand-<hex> as "empty" so the
    // proposal applies silently (instead of going to the diff panel).
    const _isPlaceholderName = (v) => {
      if (typeof v !== 'string') return true;
      const t = v.trim();
      if (!t) return true;
      return /^Brand-[0-9a-z]{4,16}$/i.test(t) || /^Untitled\b/i.test(t) || /^New brand\b/i.test(t);
    };
    if (typeof p.name === 'string' && p.name.trim() && _isPlaceholderName(name)) {
      setName(p.name.trim());
      filled.push('name');
    } else {
      fillStr(name, setName, 'name');
    }

    fillStr(description, setDescription, 'description');
    fillStr(offer, setOffer, 'offer');
    fillStr(painPoint, setPainPoint, 'painPoint');
    fillStr(emotionalAttachment, setEmotional, 'emotionalAttachment');
    fillStr(domain, setDomain, 'brandDomain');
    fillStr(website, setWebsite, 'brandWebsite');
    fillStr(globalMessage, setGlobalMessage, 'globalBrandMessageForAudience');
    fillStr(brandPostingInstructions, setBrandPostingInstructions, 'brandPostingInstructions');
    fillStr(actionType, setActionType, 'actionType');
    fillStr(actionUrl, setActionUrl, 'actionUrl');
    fillStr(modelType, setModelType, 'modelType');
    fillStr(contentType, setContentType, 'postingType');
    // Theme color defaults are '#000000' / '#ffffff' — treat those as
    // "untouched" so the proposal palette can take over.
    if (!themeName || themeName === '#000000') {
      if (typeof p.brandThemePrimaryColor === 'string') {
        setThemeName(p.brandThemePrimaryColor);
        filled.push('brandThemePrimaryColor');
      }
    }
    if (!secondaryTheme || secondaryTheme === '#ffffff') {
      if (typeof p.brandThemeSecondaryColor === 'string') {
        setSecondaryTheme(p.brandThemeSecondaryColor);
        filled.push('brandThemeSecondaryColor');
      }
    }
    fillArr(pricingInfo, setPricingInfo, 'pricingInfo');
    fillArr(technicalFaq, setTechnicalFaq, 'technicalFaq');
    fillArr(tutorialsAndTrainings, setTutorialsAndTrainings, 'tutorialsAndTrainings');
    fillArr(competitors, setCompetitors, 'competitors');
    fillStr(brandVoice, setBrandVoice, 'brandVoice');
    fillArr(brandKeywords, setBrandKeywords, 'brandKeywords');
    fillArr(brandHashtags, setBrandHashtags, 'brandHashtags');

    // modelImages: pulled from the brand's own top-engagement posts.
    // Same "don't overwrite" guard — if the user has uploaded any model
    // image (a File or {original,isUploaded:true}), leave their list alone.
    if (Array.isArray(p.modelImages) && p.modelImages.length > 0
        && (!Array.isArray(modelImages) || modelImages.length === 0)) {
      setModelImages(p.modelImages.map((url) => ({
        url, original: url, isUploaded: true, aiSourced: true,
      })));
      filled.push('modelImages');
    }

    setAiPrefilledFields((prev) => Array.from(new Set([...prev, ...filled])));
    setAiProposalDiffs((prev) => ({ ...prev, ...diffs }));
    // Pick up the latest disabledProposals map from the response so
    // toggles render in the correct state across reloads.
    if (proposalRes.disabledProposals && typeof proposalRes.disabledProposals === 'object') {
      setDisabledProposals(proposalRes.disabledProposals);
    }
    if (proposalRes.fieldSources && typeof proposalRes.fieldSources === 'object') {
      setFieldSources(proposalRes.fieldSources);
    }
    if (proposalRes.enrichedAt) setProposalEnrichedAt(proposalRes.enrichedAt);
  }, [
    // Every form value referenced inside applyProposal MUST be in
    // this deps list — otherwise the useCallback closes over a stale
    // copy and every poll cycle re-creates the same diff (so the user
    // clicks Apply, the value updates, but next poll the stale
    // closure compares "old value" vs "proposed" and the diff pops
    // right back).
    name, description, offer, painPoint, emotionalAttachment,
    domain, website, globalMessage, brandPostingInstructions,
    themeName, secondaryTheme, actionType, actionUrl,
    modelType, contentType,
    pricingInfo, technicalFaq, tutorialsAndTrainings, competitors, modelImages,
    brandVoice, brandKeywords, brandHashtags,
  ]);

  // Tracks which proposed values the user has explicitly Apply'd. Once
  // a field is in this ref, the next poll won't re-surface a diff for
  // it UNLESS the proposal value itself changes (e.g. a fresh
  // enrichment cycle proposed something different) — see fillStr's
  // gate that reads this. Without this, the user clicked Apply, the
  // form updated, but the very next poll re-created the same diff
  // and the suggestion kept popping back.
  const acceptedProposalsRef = useRef({});

  // Apply a single proposed diff — used by the per-field "Apply" buttons
  // in the AI review panel. Calls the recorded setter with the AI's
  // proposed value, then promotes the field from diffs → prefilled.
  const applyProposalField = useCallback((fieldName) => {
    setAiProposalDiffs((prev) => {
      const entry = prev[fieldName];
      if (!entry) return prev;
      if (entry.setter) entry.setter(entry.proposed);
      // Also push the value through react-hook-form so registered
      // inputs (name, domain, website, etc.) reflect the apply
      // immediately. Without this, the React useState got the new
      // value but the input element kept showing RHF's internal
      // tracked value (the user's pre-Apply input).
      try {
        if (typeof setRHFValue === 'function') {
          setRHFValue(fieldName, entry.proposed, { shouldValidate: false, shouldDirty: true });
        }
      } catch (_) { /* RHF may not track this field — non-fatal */ }
      // Lock this proposed value so the poll's next cycle doesn't
      // re-surface the same diff. We key by proposed value so a
      // future enrichment with a DIFFERENT proposal still surfaces.
      acceptedProposalsRef.current[fieldName] = entry.proposed;
      // Persist the apply to backend so:
      //   - The value lives on Brand/BrandSettings (no need to click
      //     Save afterwards to keep the change).
      //   - The backend records the acceptance and the next
      //     /proposed-fields fetch suppresses this suggestion server-
      //     side too — guarantees that even a full page reload won't
      //     re-pop it.
      //   - The response carries the freshly-updated brand; we
      //     dispatch UPDATE_BRAND_DATA so user.brands hydrates and
      //     every other consumer (kanban, brand list, etc.) sees the
      //     change without waiting for a refetch.
      if (modalBrandId) {
        acceptProposedField(modalBrandId, fieldName, entry.proposed)
          .then((res) => {
            if (res && res.brand) {
              dispatch({ type: 'UPDATE_BRAND', payload: res.brand });
            }
          })
          .catch((err) => {
            // Non-fatal — the local form value is already updated.
            // The user can click Save later to persist if the
            // backend write didn't land.
            console.warn('[wizard] accept-proposal persist failed:', err && err.message);
          });
      }
      const { [fieldName]: _drop, ...rest } = prev;
      return rest;
    });
    setAiPrefilledFields((prev) => Array.from(new Set([...prev, fieldName])));
  }, [setRHFValue, modalBrandId, dispatch]);
  const dismissProposalField = useCallback((fieldName) => {
    setAiProposalDiffs((prev) => {
      const entry = prev[fieldName];
      // Record the dismissed value to backend so the same proposal
      // stops re-popping next poll. Treat the proposed value as
      // "resolved" via the acceptedProposals map. Without this the
      // poll cycle re-creates the same diff every 5s.
      if (entry && modalBrandId) {
        acceptedProposalsRef.current[fieldName] = entry.proposed;
        dismissProposedField(modalBrandId, fieldName, entry.proposed)
          .catch((err) => console.warn('[wizard] dismiss-proposal persist failed:', err && err.message));
      }
      const { [fieldName]: _drop, ...rest } = prev;
      return rest;
    });
  }, [modalBrandId]);

  // Flip the "AI suggest" flag for a single field. enabled=false →
  // backend records the field in BrandSettings.disabledProposals so
  // the /proposed-fields endpoint stops returning it, regardless of
  // what the LLM produces. enabled=true → re-enable.
  const toggleFieldProposals = useCallback(async (fieldName, enabled) => {
    if (!modalBrandId) return;
    // Optimistic UI flip.
    setDisabledProposals((prev) => {
      const next = { ...prev };
      if (enabled) delete next[fieldName];
      else next[fieldName] = true;
      return next;
    });
    // Also drop the current diff card for this field — there's no
    // point reviewing a suggestion the user just disabled.
    if (!enabled) {
      setAiProposalDiffs((prev) => {
        const { [fieldName]: _drop, ...rest } = prev;
        return rest;
      });
    }
    try {
      const res = await toggleProposedField(modalBrandId, fieldName, enabled);
      if (res && res.disabledProposals) setDisabledProposals(res.disabledProposals);
    } catch (err) {
      console.warn('[wizard] toggle-proposal failed:', err && err.message);
    }
  }, [modalBrandId]);

  // applyProposal is re-created on every form-state change (its deps
  // include all the form fields). The pollProposal while-loop below
  // captures refreshProposal once and keeps that frozen reference for
  // its entire lifetime — so without a ref, the poll keeps running
  // applyProposal with stale form values, regenerating the same diff
  // even after the user clicks Apply.
  const applyProposalRef = useRef(applyProposal);
  useEffect(() => { applyProposalRef.current = applyProposal; }, [applyProposal]);

  const refreshProposal = useCallback(async () => {
    if (!modalBrandId) return null;
    try {
      const res = await getProposedBrandFields(modalBrandId);
      // Drop a response for a brand the user already switched away from — its
      // suggestions must NOT leak into the brand now on screen.
      if (proposalBrandRef.current && proposalBrandRef.current !== modalBrandId) return null;
      // Always call THE LATEST applyProposal via the ref. The
      // useCallback's deps used to include applyProposal, but the
      // running poll loop captured the old refreshProposal anyway
      // and the closure went stale.
      if (applyProposalRef.current) applyProposalRef.current(res);
      return res;
    } catch (e) {
      // Non-fatal — the wizard still works without the proposal.
      return null;
    }
  }, [modalBrandId]);

  // Polling controller used by both the on-mount fetch and the
  // Re-enrich-now button. Polls every 5s for up to maxMs, stopping
  // early once enrichedAt advances past the baseline (signals the
  // worker run completed) or the modal closes.
  const pollIdRef = useRef(0);
  const pollProposal = useCallback(async ({ baseline = null, maxMs = 120000, intervalMs = 5000 } = {}) => {
    if (!modalBrandId) return;
    const myId = ++pollIdRef.current;
    const started = Date.now();
    let lastSeen = baseline;
    while (pollIdRef.current === myId && (Date.now() - started) < maxMs && props.isOpen) {
      const res = await refreshProposal();
      const at = res && res.enrichedAt ? String(res.enrichedAt) : null;
      if (at && at !== lastSeen) {
        lastSeen = at;
        if (baseline && at !== baseline) break; // worker finished a new run
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
  }, [modalBrandId, refreshProposal, props.isOpen]);

  // Brand switched (or the modal re-opened on a different brand): CLEAR the
  // previous brand's AI suggestions + provenance so they never leak into
  // another brand, and re-arm the proposed-fields fetch for the new brand.
  // aiProposalDiffs merges on each poll, so without this, switching brands
  // accumulates other brands' "Suggestions for your edited fields".
  useEffect(() => {
    proposalBrandRef.current = modalBrandId;
    setAiProposalDiffs({});
    setAiPrefilledFields([]);
    acceptedProposalsRef.current = {};
    prefilledOnceRef.current = false;
  }, [modalBrandId]);

  useEffect(() => {
    if (!props.isOpen || !modalBrandId) return;
    if (prefilledOnceRef.current) return;
    prefilledOnceRef.current = true;
    // Fetch immediately, then keep polling while the modal is open
    // so a slow enrichment (LLM cold start, image vision pass, etc.)
    // still lands in the wizard's form once it completes. Without
    // this, the proposal was fetched once on open and never again —
    // so a brand committed without any user-typed data kept the
    // "Brand-<timestamp>" placeholder name forever.
    refreshProposal();
    pollProposal({ maxMs: 5 * 60 * 1000, intervalMs: 5000 });
  }, [props.isOpen, modalBrandId, refreshProposal, pollProposal]);

  useEffect(() => {
    if (!props.isOpen) prefilledOnceRef.current = false;
  }, [props.isOpen]);

  const { loading, request: createBrandAPI } = useHttp({
    requestCallback: (data) => createBrand(data),
    onLoad: (res) => {
      if (!mountedRef.current) return;
      dispatch(addNewBrand(res));
      addNotification("Brand was successfully created!", "success");
      // Promotion writes onto a brand that now exists, so it sits AFTER the save.
      setSelectedSteps(STEP.STEP_05);
    },
    onError: (res) => {
      addNotification(
        "Something went wrong while creating your brand, please contact support.",
        "danger"
      );
    },
  });

  const { loading: exportsettingsloading, request: createBrandSettingsAPI } =
    useHttp({
      requestCallback: (data) => createBrandSettings(data),
      onLoad: (res) => {
        if (!mountedRef.current) return;
        createObjectForm.brandExportSettings = res?._id;
        createBrandAPI(createObjectForm);
      },
      onError: (res) => {
        addNotification(
          "Something went wrong while creating your brand, please contact support.",
          "danger"
        );
      },
    });

  const { request: getCompanySettingsAPI } = useHttp({
    requestCallback: (data) => getCompanySettings(data),
    onLoad: (res) => {
      if (!mountedRef.current) return;
      getBrandSettingsAPI(res?.recentBrandExportSettings);
    },
    onError: (res) => {},
  });

  const { loading: exportSettingsLoading, request: getBrandSettingsAPI } =
    useHttp({
      requestCallback: (data) => getBrandSettings(data),
      onLoad: (res) => {
        if (!mountedRef.current) return;
        console.log(res);
        setMilestones(
          milestoneOptionsVar?.find((i) => i.value === res?.milestone)
        );
        setIncludeMiscellaneous(res?.includeMiscellaneous);
        setInclude2DElements(res?.include2DElements);

      },
      onError: (res) => {},
    });

  const { loading: getBrandLoading, request: getSingleBrandAPI } = useHttp({
    requestCallback: (id) => getBrandById(id),
    onLoad: (res) => {
      if (!mountedRef.current) return;
      console.log(res);
      setBrandData(res);
      const brand = { ...res };
      setName(brand.name);
      setDescription(brand.description);
      setDomain(brand?.brandDomain);
      setWebsite(brand?.brandWebsite);
      // These three were NEVER hydrated on reopen — they rendered empty (so
      // saves "looked" lost) and the next Save sent the blank state, WIPING
      // the stored values (2026-07-12).
      setOffer(brand?.offer || '');
      setPainPoint(brand?.painPoint || '');
      setEmotional(brand?.emotionalAttachment || '');
      setUsers(
        supervisorsOptions.filter((x) => brand?.users.includes(x.value))
      );
      setSupervisors(
        supervisorsOptions.filter((x) =>
          brand?.brandSettings?.listEmailSupervisors.includes(x.value)
        )
      );
      setSupervisor(
        supervisorsOptions.find(
          (x) => x.value === brand?.brandSettings?.mainEmailSupervisor
        )
      );
      setIsAuto(brand?.brandSettings?.autopost);
      // Default to true so brands created before this field existed
      // get the new informational emails by default. Once they save,
      // their explicit choice persists.
      setNotifySupervisorOnWorkflows(
        brand?.brandSettings?.notifySupervisorOnWorkflows !== false
      );
      // Auto flags surfaced in Step 03 Settings. Defaults mirror the schema/worker
      // resolver: autoGenerate on unless explicitly false; autopilot is the
      // per-feature map as stored (untouched keys fall back to their
      // effective default in the UI); newsletter off unless opted in.
      setAutoGenerate(brand?.brandSettings?.autoGenerate !== false);
      setAutopilotFlags(
        brand?.brandSettings?.autopilot && typeof brand.brandSettings.autopilot === 'object'
          ? { ...brand.brandSettings.autopilot }
          : {}
      );
      // Promotion MUST be hydrated, not left at its empty default. savePromotion
      // PUTs whatever is in this state, so an un-hydrated edit session would
      // send `{features: [], ctaText: '', ctaUrl: ''}` and silently wipe every
      // feature the brand already had. Harmless while the step was unreachable
      // from edit mode; a data-loss bug the moment it is reachable, which is
      // exactly what the fix above does.
      {
        const p = brand?.brandSettings?.promotion;
        setPromotion({
          features: Array.isArray(p?.features) ? p.features : [],
          ctaText: p?.ctaText || "",
          ctaUrl: p?.ctaUrl || "",
        });
      }
      setAutoNewsletter(brand?.brandSettings?.autoNewsletter === true);
      setAutoNewsletterFrequency(brand?.brandSettings?.autoNewsletterFrequency || "weekly");
      setLanguage(brand?.brandSettings?.language);
      setBrandPostingInstructions(brand?.brandSettings?.brandPostingInstructions);
      setLogoUrl(brand?.brandSettings?.logoUrl || "");
    setImageFallbackTheme(brand?.brandSettings?.imageFallbackTheme || "");
      setOverlayLogoUrl(brand?.brandSettings?.overlayLogoUrl || "");
      setGlobalMessage(brand?.brandSettings?.globalBrandMessageForAudience);
      setThemeName(brand?.brandSettings?.brandThemePrimaryColor);
      setSecondaryTheme(brand?.brandSettings?.brandThemeSecondaryColor);
      setContentType(brand?.brandSettings?.postingType);
      setBrandPostingCadence(brand?.brandSettings?.postingCadence);
      setModelType(brand?.brandSettings?.modelType);
      setBrandPostingCadence(brand?.brandSettings?.postingCadence);
      setModelType(brand?.brandSettings?.modelType);
      setPostingTime(brand?.brandSettings?.postingTime);
      setActionType(brand?.brandSettings?.actionType ?? '');
      setActionUrl(brand?.brandSettings?.actionUrl ?? '');

      if (brand.img) {
        (async () => {
          const imgUrl = await extractImageFromZip(brand.img);
          setBrandImage({ url: imgUrl, isUploaded: true, original: brand.img });
        })();
      }

      if (brand.brandSettings?.modelImages?.length > 0) {
        // The previous code created `let images = []`, fired async
        // IIFEs that pushed into it, then called setModelImages(images)
        // synchronously — the IIFEs hadn't resolved yet, so the modal
        // always re-opened with an empty reference-images list. Await
        // the lot before setting state so the user actually sees their
        // saved images on reopen.
        (async () => {
          try {
            const resolved = await Promise.all(
              brand.brandSettings.modelImages.map(async (image) => {
                const imgUrl = await extractImageFromZip(image);
                return { url: imgUrl, isUploaded: true, original: image };
              })
            );
            if (mountedRef.current) setModelImages(resolved);
          } catch (_) { /* keep modal open even if a single thumb fails */ }
        })();
      }
      setBrandSettings(res?.brandSettings || null);
      if (res?.brandSettings && typeof res.brandSettings.useBrandProfile === 'boolean') {
        setUseBrandProfile(res.brandSettings.useBrandProfile);
      }
      if (res?.brandSettings) {
        setPricingInfo(Array.isArray(res.brandSettings.pricingInfo) ? res.brandSettings.pricingInfo : []);
        setTechnicalFaq(Array.isArray(res.brandSettings.technicalFaq) ? res.brandSettings.technicalFaq : []);
        setTutorialsAndTrainings(Array.isArray(res.brandSettings.tutorialsAndTrainings) ? res.brandSettings.tutorialsAndTrainings : []);
        setCompetitors(Array.isArray(res.brandSettings.competitors) ? res.brandSettings.competitors : []);
        // Voice fields — populated by enrichment force-fill OR by the
        // user editing the new inputs in step 02. Reopen always
        // shows whatever was last saved.
        setBrandVoice(typeof res.brandSettings.brandVoice === 'string' ? res.brandSettings.brandVoice : '');
        setContentAlignment(
          (Array.isArray(res.brandSettings.contentReferences) && res.brandSettings.contentReferences[0] && res.brandSettings.contentReferences[0].content) || ''
        );
        setContentRulesText(Array.isArray(res.brandSettings.contentRules) ? res.brandSettings.contentRules.join('\n') : '');
        setBrandKeywords(Array.isArray(res.brandSettings.brandKeywords) ? res.brandSettings.brandKeywords : []);
        setBrandHashtags(Array.isArray(res.brandSettings.brandHashtags) ? res.brandSettings.brandHashtags : []);
      }
      dispatch(updateBrandData(res)); // <— ensures Redux has latest byPlatform
    },
    onError: (res) => {},
  });

  const { loading: getBrandEditLoading, request: updateBrandApi } = useHttp({
    requestCallback: (id) => editBrandPut(id),
    onLoad: (res) => {
      if (!mountedRef.current) return;
      dispatch(updateBrandData(res));
      addNotification("Brand was successfully updated!", "success");
      // Advance to Promotion rather than closing. Editing a brand used to CLOSE
      // the modal here, which made Promotion unreachable from edit mode
      // entirely — the only way to it was creating a brand from scratch. The
      // success step still closes, and the header quick-save (handleSaveOnly)
      // is untouched for anyone who just wants to save and stay put.
      setSelectedSteps(STEP.STEP_05);
    },
    onError: (res) => {},
  });

  useEffect(() => {
    if (selectedSteps === STEP.STEP_01) {
      clearSocialDraftsAndCaches();
    }
  }, [selectedSteps, clearSocialDraftsAndCaches]);

  useEffect(() => {
    if (props.brand) {
      getSingleBrandAPI(props.brand);
    }
  }, [props.brand]);

  // Edit-mode live sync is now handled by `brandSettingsFromRedux`
  // useSelector above — the wizard's brandSettings is derived from
  // Redux directly, so this mirror useEffect is no longer needed.

  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  const createObject = async (e) => {
    e.preventDefault();

    if (brandImage?.isUploaded) {
      createObjectForm.img = brandImage.original;
    } else if (brandImage instanceof File) {
      setImageUploadLoading(true);
      const brandImageUrl = await uploadFileToServer(brandImage);
      createObjectForm.img = brandImageUrl;
      setImageUploadLoading(false);
    }

    createObjectForm.modelImages = [];
    const models = Array.isArray(modelImages)
      ? modelImages.filter((img) => img instanceof File)
      : [];
    const uploaded = Array.isArray(modelImages)
      ? modelImages.filter((img) => img?.isUploaded).map((img) => img.original)
      : [];

    if (models.length > 0) {
      setImageUploadLoading(true);
      const modelImageUrls = await Promise.all(
        models.map(async (image) => {
          return await uploadFileToServer(image);
        })
      );
      createObjectForm.modelImages = modelImageUrls;
      setImageUploadLoading(false);
    }
    createObjectForm.modelImages = [
      ...uploaded,
      ...createObjectForm.modelImages,
    ];

    createObjectForm.companyId = userInfoRedux?.company;
    createObjectForm.name = name;
    createObjectForm.description = description;
    createObjectForm.brandDomain = domain;
    createObjectForm.brandWebsite = website;
    createObjectForm.offer = offer;
    createObjectForm.painPoint = painPoint;
    createObjectForm.emotionalAttachment = emotionalAttachment;
    createObjectForm.users = users.map(x => x.value);

    createObjectForm.brandSettings = {
      autopost: isAuto,
      notifySupervisorOnWorkflows,
      autoGenerate,
      // Full merged per-feature map — backend $set replaces the whole
      // autopilot subdoc, so sending the complete object avoids dropping
      // previously-set flags.
      autopilot: autopilotFlags,
      autoNewsletter,
      autoNewsletterFrequency,
      language,
      postingType: contentType,
      postingTime,
      postingCadence: brandPostingCadence,
      modelType,
      brandThemePrimaryColor: themeName,
      brandThemeSecondaryColor: secondaryTheme,
      brandPostingInstructions,
      imageFallbackTheme,
      logoUrl,
      overlayLogoUrl,
      globalBrandMessageForAudience: globalMessage,
      actionType,
      actionUrl,
      useBrandProfile,
      pricingInfo,
      technicalFaq,
      tutorialsAndTrainings,
      competitors,
      brandVoice,
      brandKeywords,
      brandHashtags,
      contentReferences: contentAlignment.trim()
        ? [{ label: 'Brand alignment content', content: contentAlignment }]
        : [],
      contentRules: contentRulesText.split('\n').map((r) => r.trim()).filter(Boolean),
      mainEmailSupervisor: supervisor?.value,
      listEmailSupervisors: supervisors.map(x => x.value),
      // DO NOT include socialAccounts / socialTargets / adsAccounts
      // here. The wizard form doesn't manage them — they're owned by
      // the OAuth connect/disconnect flow and live on the backend.
      // Including an empty array (the previous behavior) wiped the
      // brand's real connections on every Save, which is why the
      // table looked empty after Save and the user thought the data
      // was non-persistent.
      modelImages: createObjectForm.modelImages || [],
    };

    if (!props.brand) {
      // If the user already connected at least one social platform during
      // Step 01, a draft brand+brandSettings was created server-side and
      // recorded in sessionStorage by startConnect. Commit that draft
      // (rename + flip deleted:false + apply patches) instead of minting a
      // second brand — otherwise the connected social accounts orphan onto
      // the abandoned draft.
      let draftIds = null;
      try {
        draftIds = JSON.parse(sessionStorage.getItem('brandConnectCtx') || 'null');
      } catch { draftIds = null; }

      if (draftIds && draftIds.brandId) {
        try {
          // socialAccounts / socialTargets / adsAccounts on brandSettings
          // are references populated by the OAuth callback. Don't send the
          // empty form arrays — $set would wipe the connections we just made.
          const {
            socialAccounts: _ignoredAccs,
            socialTargets: _ignoredTgts,
            adsAccounts: _ignoredAds,
            byPlatform: _ignoredBP,
            selectedByPlatform: _ignoredSel,
            ...settingsPatch
          } = createObjectForm.brandSettings || {};

          // Stamp the brand with the user's browser timezone so the
          // worker's 3x/day cron fires at local 7am/12pm/5pm for THIS
          // brand, not the server's UTC. Falls back to UTC if Intl is
          // unavailable (very old browsers).
          let _detectedTz = 'UTC';
          try {
            _detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
          } catch { _detectedTz = 'UTC'; }

          const committed = await commitBrandConnectSession({
            brandId: draftIds.brandId,
            name: createObjectForm.name,
            description: createObjectForm.description,
            img: createObjectForm.img,
            brandPatch: {
              brandDomain: createObjectForm.brandDomain,
              brandWebsite: createObjectForm.brandWebsite,
              offer: createObjectForm.offer,
              painPoint: createObjectForm.painPoint,
              emotionalAttachment: createObjectForm.emotionalAttachment,
              users: createObjectForm.users,
            },
            settingsPatch: { ...settingsPatch, timezone: _detectedTz },
          });
          if (!mountedRef.current) return;
          dispatch(addNewBrand(committed));
          try { sessionStorage.removeItem('brandConnectCtx'); } catch {}
          addNotification("Brand was successfully created!", "success");
          // Promotion, NOT the success screen. This jumped straight to STEP_06
          // and skipped Promotion entirely, so the step was unreachable on the
          // path almost everyone takes — the legacy createBrand path below has
          // always gone to STEP_05 correctly, which is why the step looked fine
          // in isolation. Promotion writes onto a brand that now exists, so it
          // has to run after the save, not instead of it.
          setSelectedSteps(STEP.STEP_05);
          return;
        } catch (err) {
          addNotification(
            "Something went wrong while creating your brand, please contact support.",
            "danger"
          );
          return;
        }
      }

      await createBrandAPI(createObjectForm);
    } else {
      createObjectForm._id = props.brand;
      await updateBrandApi(createObjectForm);
    }
  };

  // Quick-save for edit mode — same payload as the full wizard submit, but
  // leaves the modal open so the user can keep refining other steps.
  const [quickSaving, setQuickSaving] = useState(false);
  const handleSaveOnly = async () => {
    if (!props.brand) return;
    setQuickSaving(true);
    try {
      const form = {};

      if (brandImage?.isUploaded) {
        form.img = brandImage.original;
      } else if (brandImage instanceof File) {
        const brandImageUrl = await uploadFileToServer(brandImage);
        form.img = brandImageUrl;
      }

      const models = Array.isArray(modelImages)
        ? modelImages.filter((img) => img instanceof File)
        : [];
      const uploaded = Array.isArray(modelImages)
        ? modelImages.filter((img) => img?.isUploaded).map((img) => img.original)
        : [];
      let uploadedModels = [];
      if (models.length > 0) {
        uploadedModels = await Promise.all(
          models.map((image) => uploadFileToServer(image))
        );
      }
      form.modelImages = [...uploaded, ...uploadedModels];

      form._id = props.brand;
      form.companyId = userInfoRedux?.company;
      form.name = name;
      form.description = description;
      form.brandDomain = domain;
      form.brandWebsite = website;
      form.offer = offer;
      form.painPoint = painPoint;
      form.emotionalAttachment = emotionalAttachment;
      form.users = users.map(x => x.value);
      form.brandSettings = {
        autopost: isAuto,
        notifySupervisorOnWorkflows,
        autoGenerate,
        autopilot: autopilotFlags,
        autoNewsletter,
        autoNewsletterFrequency,
        language,
        postingType: contentType,
        postingTime,
        postingCadence: brandPostingCadence,
        modelType,
        brandThemePrimaryColor: themeName,
        brandThemeSecondaryColor: secondaryTheme,
        brandPostingInstructions,
        imageFallbackTheme,
        logoUrl,
        overlayLogoUrl,
        globalBrandMessageForAudience: globalMessage,
        actionType,
        actionUrl,
        useBrandProfile,
        pricingInfo,
        technicalFaq,
        tutorialsAndTrainings,
        competitors,
        brandVoice,
        brandKeywords,
        brandHashtags,
        // Brand canon — this quick-save path (the modal's main Save button)
        // used to omit these, so rules typed in the wizard were silently
        // never sent (2026-07-12: "all the rules I put there are gone").
        contentReferences: contentAlignment.trim()
          ? [{ label: 'Brand alignment content', content: contentAlignment }]
          : [],
        contentRules: contentRulesText.split('\n').map((r) => r.trim()).filter(Boolean),
        mainEmailSupervisor: supervisor?.value,
        listEmailSupervisors: supervisors.map(x => x.value),
        modelImages: form.modelImages || [],
      };

      const res = await editBrandPut(form);
      if (!mountedRef.current) return;
      dispatch(updateBrandData(res));
      addNotification("Brand saved.", "success");
    } catch (err) {
      addNotification(
        "Something went wrong while saving, please contact support.",
        "danger"
      );
    } finally {
      if (mountedRef.current) setQuickSaving(false);
    }
  };

  const handleBrandImg = (e) => setBrandImage(e.target.files[0] || null);
  const handleModelImages = (e) => {
    const files = Array.from(e.target.files || []);
    setModelImages((prev) => [...prev, ...files]);
  };

  const handleRemoveImage = (index) => {
    const updatedImages = modelImages.filter((_, i) => i !== index);
    setModelImages(updatedImages);
  };

  const handleIfcFile = async (e) => {
    const selectedIfc = e.target.files[0];
    setIfcFile(selectedIfc);
    const fileUrl = await uploadFileToServer(selectedIfc);
    setIfcUrl(fileUrl);
  };

  const checkBrandName = async (value) => {
    setBrandNumberLoading(true);
    try {
      const res = await verifyBrandName({ name: value });
      setBrandNumberLoading(false);
      if (res.isBrandNameUnique) {
        setBrandNumber(value);
      } else {
        setError("brandNo", { type: "custom", message: res.err });
        setBrandNumber("");
      }
    } catch (error) {
      setError("brandNo", { type: "custom", message: error.err });
      setBrandNumber("");
      setBrandNumberLoading(false);
    }
  };

  const debouncedApiCall = debounce(checkBrandName, 1000);

  return (
    <MDBModal
      isOpen={props.isOpen}
      toggle={choseToggle(loading, toggle)}
      centered
      className="create-modal-full-size"
    >
      <MDBModalHeader toggle={choseToggle(loading, toggle)}>
        <div className="d-flex align-items-center justify-content-between flex-wrap w-100" style={{ gap: 12 }}>
          <div className="d-flex align-items-center">
            <span>{props.brand ? `Edit brand${name ? ` — ${name}` : ""}` : "Create brand"}</span>
            {props.brand && (
              <button
                type="button"
                className="header-btn text-white bg-bid border-bid ml-3 py-1 px-3"
                style={{ fontSize: 14 }}
                onClick={handleSaveOnly}
                disabled={quickSaving || loading}
              >
                {quickSaving ? "Saving…" : "Save"}
              </button>
            )}
          </div>
          <div className="d-flex align-items-center" style={{ gap: 12, fontSize: 13 }}>
            {props.brand && (
              <button
                type="button"
                className="border rounded px-3 py-1"
                style={{ fontSize: 13, background: 'transparent' }}
                disabled={enriching}
                onClick={async () => {
                  setEnriching(true);
                  try {
                    const { post } = await import("api/index");
                    await post(`/brands/${props.brand}/enrich`, {});
                    addNotification(
                      "Re-enrichment queued — empty fields will refill as the worker streams results.",
                      "success"
                    );
                    // Snapshot the current enrichedAt so the poll loop
                    // can detect when the new run finishes (the value
                    // advances). Polls every 5s for up to 2min. Per
                    // policy we never overwrite values the user has
                    // typed in.
                    pollProposal({ baseline: proposalEnrichedAt, maxMs: 120000, intervalMs: 5000 });
                  } catch (e) {
                    addNotification((e && e.message) || "Failed to queue re-enrichment", "danger");
                  } finally { setEnriching(false); }
                }}
                title="Re-fire the AI brand profile build using the latest fields + connected accounts."
              >
                {enriching ? "Queueing…" : "Re-enrich now"}
              </button>
            )}
            <button
              type="button"
              className="border rounded px-3 py-1"
              style={{ fontSize: 13, background: previewOpen ? '#eef' : 'transparent' }}
              onClick={() => setPreviewOpen((o) => !o)}
            >
              {previewOpen ? "Hide preview" : "Preview"}
            </button>
            <label className="d-flex align-items-center mb-0" style={{ gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={useBrandProfile}
                onChange={(e) => setUseBrandProfile(e.target.checked)}
              />
              <span>Use AI-enriched profile</span>
            </label>
          </div>
        </div>
      </MDBModalHeader>
      <MDBModalBody className="pt-0 ov-none">
        {/* Top-of-page audience-desire one-liner — the highest-heuristic thing
            this brand's audience wants, shown right after the header. */}
        <AudienceDesireBanner brandSettings={brandSettings} />
        {previewOpen && (
          <BrandProfilePreview
            brandSettings={brandSettings}
            useBrandProfile={useBrandProfile}
            overrides={{
              name,
              emotionalAttachment,
              tone: brandVoice,
              primaryColor: themeName,
              secondaryColor: secondaryTheme,
            }}
          />
        )}
        <form onSubmit={(e) => createObject(e)}>
          <div>
            {showProposalPanel && selectedSteps !== STEP.STEP_06 && (aiPrefilledFields.length > 0 || Object.keys(aiProposalDiffs).length > 0) && (
              <div
                className="border rounded mb-2"
                style={{
                  background: '#f0f6ff',
                  borderColor: '#cdddff',
                  fontSize: 13,
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-between px-3 py-2"
                  style={{ borderBottom: '1px solid #cdddff' }}
                >
                  <span>
                    <strong>🤖 AI suggestions ready</strong>
                    {' — '}
                    {aiPrefilledFields.length > 0 && (
                      <>
                        <span className="text-success">
                          {aiPrefilledFields.length} field{aiPrefilledFields.length === 1 ? '' : 's'} prefilled
                        </span>
                        {Object.keys(aiProposalDiffs).length > 0 ? ', ' : ''}
                      </>
                    )}
                    {Object.keys(aiProposalDiffs).length > 0 && (
                      <span className="text-warning">
                        {Object.keys(aiProposalDiffs).length} suggestion{Object.keys(aiProposalDiffs).length === 1 ? '' : 's'} for fields you've already edited
                      </span>
                    )}
                    {proposalEnrichedAt && (
                      <span className="text-muted" style={{ fontSize: 11 }}>
                        {' '}· enriched {new Date(proposalEnrichedAt).toLocaleString()}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="border rounded px-2 py-1"
                    style={{ fontSize: 12, background: 'transparent' }}
                    onClick={() => setShowProposalPanel(false)}
                    title="Hide this panel; your edits + applied suggestions are kept."
                  >
                    Hide
                  </button>
                </div>

                {aiPrefilledFields.length > 0 && (
                  <div className="px-3 py-2" style={{ background: '#e7f5ec', borderBottom: '1px solid #cdddff' }}>
                    <div className="f-12 text-muted text-uppercase mb-1">Prefilled empty fields</div>
                    <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                      {aiPrefilledFields.map((f) => {
                        const aiOff = disabledProposals[f] === true;
                        return (
                          <span
                            key={f}
                            className="border rounded px-2 py-1 d-inline-flex align-items-center"
                            style={{ fontSize: 12, background: '#fff', borderColor: '#b7dfc4', gap: 6 }}
                            title={aiOff ? "AI suggestions disabled for this field." : "Auto-filled because the field was empty. Click the AI toggle to stop future suggestions."}
                          >
                            ✓ {FIELD_LABELS[f] || f}
                            <button
                              type="button"
                              onClick={() => toggleFieldProposals(f, aiOff /* clicking when off → re-enable */)}
                              style={{ fontSize: 10, padding: '0 4px', border: '1px solid #ddd', background: aiOff ? '#f1f1f1' : '#fff', color: aiOff ? '#888' : '#3a6ea5', borderRadius: 3 }}
                              title={aiOff ? "Re-enable AI suggestions for this field" : "Stop AI from proposing this field"}
                            >
                              AI: {aiOff ? 'off' : 'on'}
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {Object.keys(aiProposalDiffs).length > 0 && (
                  <div className="px-3 py-2">
                    <div className="f-12 text-muted text-uppercase mb-2">
                      Suggestions for your edited fields (review &amp; apply if you want)
                    </div>
                    <div className="d-flex flex-column" style={{ gap: 10 }}>
                      {Object.entries(aiProposalDiffs).map(([field, diff]) => {
                        const fmt = (v) => {
                          if (Array.isArray(v)) return v.slice(0, 5).join(', ') + (v.length > 5 ? ` (+${v.length - 5} more)` : '');
                          return String(v).slice(0, 200) + (String(v).length > 200 ? '…' : '');
                        };
                        return (
                          <div
                            key={field}
                            className="border rounded p-2"
                            style={{ background: '#fff', borderColor: '#f0d9a8' }}
                          >
                            <div className="d-flex align-items-center justify-content-between mb-1" style={{ gap: 8 }}>
                              <strong style={{ fontSize: 12 }}>{FIELD_LABELS[field] || field}
                                {fieldSources[field] && (
                                  <span className="ml-1 px-1 rounded" style={{ fontSize: 9, fontWeight: 600, background: fieldSources[field] === 'manual' ? '#ede9fe' : '#dbeafe', color: fieldSources[field] === 'manual' ? '#6d28d9' : '#1e40af' }}
                                    title={fieldSources[field] === 'manual' ? 'You set this value manually — the AI won’t overwrite it.' : 'This value came from an accepted/auto AI suggestion.'}>
                                    {fieldSources[field] === 'manual' ? 'Manual' : 'AI'}
                                  </span>
                                )}
                              </strong>
                              <div className="d-flex" style={{ gap: 4 }}>
                                <button
                                  type="button"
                                  className="border rounded px-2 py-1"
                                  style={{ fontSize: 11, background: 'transparent', color: '#888' }}
                                  onClick={() => toggleFieldProposals(field, false)}
                                  title="Stop AI from proposing changes to this field. Click again to re-enable."
                                >
                                  AI: on → off
                                </button>
                                <button
                                  type="button"
                                  className="border rounded px-2 py-1 text-white"
                                  style={{ fontSize: 11, background: '#3a6ea5', borderColor: '#3a6ea5' }}
                                  onClick={() => applyProposalField(field)}
                                  title="Replace your current value with the AI suggestion"
                                >
                                  Apply
                                </button>
                                <button
                                  type="button"
                                  className="border rounded px-2 py-1"
                                  style={{ fontSize: 11, background: 'transparent' }}
                                  onClick={() => dismissProposalField(field)}
                                  title="Keep your current value, dismiss the suggestion"
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                            <div style={{ fontSize: 11, color: '#666' }}>
                              <div><strong>You have:</strong> {fmt(diff.current) || '(empty)'}</div>
                              <div><strong>AI suggests:</strong> {fmt(diff.proposed)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {selectedSteps !== STEP.STEP_06 && (
              <BrandWizardStepper
                current={selectedSteps}
                problemsByStep={stepProblems}
                onJump={goToStep}
                images={{
                  current: StepInProgressImg,
                  done: StepDoneImg,
                  todo: StepIncompleteImg,
                }}
              />
            )}
          </div>

          {/* Step 01 */}
          {selectedSteps === STEP.STEP_01 ? (
            <>
              <div id="brand-social-accounts-panel">
              <SocialMediaAccounts
                key={brandSettingsId || _id || 'new'}
                brandId={_id || null}
                brandSettingsId={brandSettingsId || null}
                brandSettings={
                  brandSettings &&
                  (brandSettings.byPlatform ||
                   brandSettings.socialAccounts ||
                   brandSettings.socialTargets)
                    ? brandSettings
                    : undefined
                }
                // EXPLICIT create-mode signal. Was previously inferred
                // from "!brandId && !brandSettingsId" inside SMA, but
                // startConnect mints draft IDs server-side mid-flow,
                // so those props flip to truthy partway through and
                // disable the create-mode pickedByPlatform override.
                // props.brand is the only stable wizard-side signal —
                // it's set only when editing an existing committed
                // brand.
                isCreating={!props.brand}
              />
              </div>
              <div className="d-flex align-items-center justify-content-between mt-5">
                <button
                  type="button"
                  className="header-btn text-bid border-bid"
                  onClick={toggle}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="header-btn text-white bg-bid border-bid mr-0"
                  onClick={() => goToStep(STEP.STEP_02)}
                >
                  Next
                  <span className="ml-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="12"
                      viewBox="0 0 8 12"
                      fill="none"
                    >
                      <path
                        d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                        fill="white"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            </>
          ) : null}
          {selectedSteps === STEP.STEP_02 ? (
            <>
              <div className="form-group mt-3 w-100">
                <label
                  htmlFor=""
                  className="f-14 inter text-black font-weight-500"
                >
                  Brand Name<FieldBadge source={fieldSources.name} />                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Brand name"
                  value={name}
                  {...register("name", {
                    // Optional: enrichment auto-fills if missing. The
                    // creator gets a setup-needed notification + email
                    // if something the pipeline truly needs is still
                    // empty after the worker runs.
                    maxLength: 20,
                    // No length-error toggle — empty is allowed; the
                    // setup-needed email + bell notification handle the
                    // case where the brand needs a real name later.
                    onChange: (e) => { setName(e.target.value); },
                  })}
                />
                {/* Brand-name "required" UI removed — every field
                    is optional in the wizard now. Setup-needed
                    notification + email tells the owner what's
                    truly missing for posting. */}
                <div className="form-group mt-3">
                  <label
                    htmlFor=""
                    className="f-14 inter text-black font-weight-500"
                  >
                    Description<FieldBadge source={fieldSources.description} />
                  </label>
                  <textarea
                    onChange={(e) => setDescription(e.target.value)}
                    type="text"
                    value={description}
                    className="form-control"
                    placeholder="Description"
                  ></textarea>
                </div>
                <div className="d-flex justify-content-between gap-2">
                  <div className="form-group mt-3 w-50">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Brand Industry                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Brand Industry"
                      value={domain}
                      {...register("domain", {
                        // Optional: enrichment fills brandDomain.
                        maxLength: 20,
                        onChange: (e) => {
                          setDomain(e.target.value);
                          if (e.target.value.length > 0) {
                            setDomainError(false);
                          } else {
                            setDomainError(true);
                          }
                        },
                      })}
                    />
                    {domainError && (
                      <p role="alert" className="validation-error-msg">
                        Please add brand domain!
                      </p>
                    )}
                  </div>
                  <div className="form-group mt-3 w-50">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Brand Website<FieldBadge source={fieldSources.brandWebsite} />                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={website}
                      placeholder="Brand Website"
                      {...register("website", {
                        // Optional: enrichment fills brandWebsite.
                        maxLength: 20,
                        onChange: (e) => {
                          setWebsite(e.target.value);
                          if (e.target.value.length > 0) {
                            setWebsiteError(false);
                          } else {
                            setWebsiteError(true);
                          }
                        },
                      })}
                    />
                    {websiteError && (
                      <p role="alert" className="validation-error-msg">
                        Please add brand website!
                      </p>
                    )}
                  </div>
                </div>
                <div className="d-flex justify-content-between gap-2">
                  <div className="form-group mt-3 w-50">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Top issue faced by your clients<FieldBadge source={fieldSources.painPoint} />
                                         </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Top Issue"
                      value={painPoint}
                      {...register("painPoint", {
                        // Optional: enrichment fills painPoint.
                        maxLength: 20,
                        onChange: (e) => {
                          setPainPoint(e.target.value);
                        },
                      })}
                    />
                  </div>
                  <div className="form-group mt-3 w-50">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Best solution fulfilled for your clients<FieldBadge source={fieldSources.offer} />
                                         </label>
                    <input
                      type="text"
                      className="form-control"
                      value={offer}
                      placeholder="Best Solution"
                      {...register("offer", {
                        // Optional: enrichment fills offer.
                        maxLength: 20,
                        onChange: (e) => {
                          setOffer(e.target.value);
                        },
                      })}
                    />
                  </div>
                </div>
                <div className="form-group mt-3 w-100">
                  <label
                    htmlFor=""
                    className="f-14 inter text-black font-weight-500"
                  >
                    Client's Emotional buy in<FieldBadge source={fieldSources.emotionalAttachment} />
                                     </label>
                  <input
                    type="text"
                    className="form-control"
                    value={emotionalAttachment}
                    placeholder="What they will feel after buying your services"
                    {...register("emotionalAttachment", {
                      // Optional: enrichment fills emotionalAttachment.
                      maxLength: 20,
                      onChange: (e) => {
                        setEmotional(e.target.value);
                      },
                    })}
                  />
                </div>

                <ChipListField
                  label="Pricing / plans"
                  hint="Each entry: one SKU, plan, or price range (e.g. 'Starter — $19/mo, 5 users')."
                  items={pricingInfo}
                  onChange={setPricingInfo}
                  source={fieldSources.pricingInfo}
                  placeholder="Add a pricing item and press Enter"
                />
                <ChipListField
                  label="Technical FAQ"
                  hint="Each entry: one Q&A or one fact. Used by the chat-support layer."
                  items={technicalFaq}
                  onChange={setTechnicalFaq}
                  source={fieldSources.technicalFaq}
                  placeholder="Add a FAQ item and press Enter"
                />
                <ChipListField
                  label="Tutorials & trainings"
                  hint="Each entry: one tutorial title, URL, or brief blurb."
                  items={tutorialsAndTrainings}
                  onChange={setTutorialsAndTrainings}
                  source={fieldSources.tutorialsAndTrainings}
                  placeholder="Add a tutorial and press Enter"
                />
                <ChipListField
                  label="Competitors"
                  hint="Each entry: one competitor, ideally with a 1-line differentiator."
                  items={competitors}
                  onChange={setCompetitors}
                  source={fieldSources.competitors}
                  placeholder="Add a competitor and press Enter"
                />

                <div className="form-group-div mt-3">
                  <label className="f-14 inter text-black font-weight-500">
                    Brand voice<FieldBadge source={fieldSources.brandVoice} />
                  </label>
                  <p className="f-12 text-muted mb-1">
                    1-2 sentences describing how the brand should sound (tone,
                    cadence, words to use). The AI enricher will fill this from
                    your connected channels if you leave it blank.
                  </p>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={brandVoice}
                    onChange={(e) => setBrandVoice(e.target.value)}
                    placeholder="e.g. Direct, founder-voice, short sentences. We swear sparingly. We never sound corporate."
                  />
                </div>

                {/* Content alignment: the canonical reference content EVERY
                    generated artifact for this brand is verified/transformed
                    against (posts + variants, FAQs, articles, emails, chat,
                    connector outreach). The reference wins over the draft. */}
                <div className="form-group-div mt-3">
                  <label className="f-14 inter text-black font-weight-500">
                    Content alignment &amp; references
                  </label>
                  <textarea
                    className="form-control"
                    rows={6}
                    value={contentAlignment}
                    onChange={(e) => setContentAlignment(e.target.value)}
                    placeholder="Paste the canonical content everything must align with: product facts, positioning, terminology, citation standards, pricing, allowed claims…"
                  />
                  <label className="f-14 inter text-black font-weight-500 mt-2">
                    Content rules (one per line)
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={contentRulesText}
                    onChange={(e) => setContentRulesText(e.target.value)}
                    placeholder={"Never promise a refund window longer than 30 days\nAlways cite the verse number when quoting\nNever mention competitor names"}
                  />
                </div>

                <ChipListField
                  label="Brand keywords"
                  hint="Topics + recurring themes the content should orbit. Used by content generation."
                  items={brandKeywords}
                  onChange={setBrandKeywords}
                  source={fieldSources.brandKeywords}
                  placeholder="Add a keyword and press Enter"
                />

                <ChipListField
                  label="Brand hashtags"
                  hint="Hashtags appended to posts where the platform supports them."
                  items={brandHashtags}
                  onChange={setBrandHashtags}
                  source={fieldSources.brandHashtags}
                  placeholder="Add a hashtag (e.g. #Marketing) and press Enter"
                />

                <div className="form-group-div mt-3">
                  <label className="f-14 inter text-black font-weight-500">
                    Add Members
                  </label>
                  <Select
                    options={supervisorsOptions}
                    value={users}
                    isMulti
                    onChange={(options) => setUsers(options)}
                  />
                </div>

                {selectedBrandType === PROJ_TYPES.IFC && (
                  <div className="form-group mt-3">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      IFC file
                    </label>
                    <div className="d-flex align-items-center justify-content-between">
                      <div
                        className="profile-upload-box cursor-pointer w-fill d-flex align-items-center my-0 justify-content-between"
                        onClick={() => ifcFileInputRef.current.click()}
                      >
                        <div className="text-left">
                          {ifcFile ? (
                            ifcFile.name
                          ) : (
                            <p className="font-weight-bold text-black f-14">
                              Click to upload{" "}
                              <span className="font-weight-light">
                                or drag and drop
                              </span>
                            </p>
                          )}
                          <p className="f-12">.IFC</p>
                        </div>
                        <img src={UploadIcon} className="upload-icon" />
                      </div>
                    </div>
                    <input
                      ref={ifcFileInputRef}
                      onChange={handleIfcFile}
                      type="file"
                      id="ifcFile"
                      name="ifcFile"
                      accept={".ifc"}
                      style={{ display: "none" }}
                    />
                  </div>
                )}

                <div className="form-group mt-3">
                  <label
                    htmlFor=""
                    className="f-14 inter text-black font-weight-500"
                  >
                    Brand image
                  </label>
                  <div className="d-flex align-items-center justify-content-between">
                    <div
                      className="profile-upload-box cursor-pointer w-fill d-flex align-items-center my-0 justify-content-between"
                      onClick={() => brandImgInputRef.current.click()}
                    >
                      <div className="text-left">
                        <p className="font-weight-bold text-black f-14">
                          Click to upload{" "}
                          <span className="font-weight-light">
                            or drag and drop
                          </span>
                        </p>
                        <p className="f-12">
                          SVG, PNG, JPG or GIF (max. 800x400px)
                        </p>
                      </div>
                      <img src={UploadIcon} className="upload-icon" />
                    </div>
                    <img
                      src={
                        brandImage && brandImage.isUploaded
                          ? brandImage.url
                          : brandImage
                          ? window.URL.createObjectURL(brandImage)
                          : UploadIcon
                      }
                      className="uploaded-icon"
                    />
                  </div>

                  <input
                    ref={brandImgInputRef}
                    onChange={handleBrandImg}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-5">
                <button
                  type="button"
                  className="header-btn text-bid border-bid"
                  onClick={() => goToStep(STEP.STEP_01)}
                >
                  <span className="mr-3 transform-180">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="12"
                      viewBox="0 0 8 12"
                      fill="none"
                    >
                      <path
                        d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                        fill="#09121F"
                      />
                    </svg>
                  </span>
                  Back
                </button>
                <button
                  type="button"
                  className="header-btn text-white bg-bid border-bid mr-0"
                  onClick={(e) => {
                    // Still no empty-field gate — every wizard step is
                    // optional, enrichment fills, and a setup-needed
                    // notification + email tells the owner what truly blocks
                    // posting. goToStep only stops a value that was TYPED and
                    // cannot be used (see brandWizardSteps).
                    goToStep(STEP.STEP_03);
                  }}
                >
                  Next
                  <span className="ml-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="12"
                      viewBox="0 0 8 12"
                      fill="none"
                    >
                      <path
                        d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                        fill="white"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            </>
          ) : null}
          {/* Step 02 */}
          {selectedSteps === STEP.STEP_03 ? (
            <>
              <div className="form-02">
                <div className="d-flex justify-content-between gap-2">
                  <div className="form-group mt-3 w-50 position-relative align-items-center">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Primary Brand Theme Color<FieldBadge source={fieldSources.brandThemePrimaryColor} />{" "}
                                         </label>

                    <input
                      type="color"
                      className="w-12 h-10 hidden color-input border rounded-md cursor-pointer"
                      value={toHexColor(themeName)}
                      ref={primaryColorPickerRef}
                      onChange={(e) => setThemeName(e.target.value)}
                    />

                    <div className="d-flex align-items-center w-100 position-relative">
                      <input
                        type="text"
                        className="form-control pe-5"
                        placeholder="#1d4ed8 or rgba(29,78,216,1)"
                        value={themeName}
                        {...register("theme", {
                          // Optional: enrichment fills primary color.
                          maxLength: 30,
                          validate: (v) => !v || isValidColor(v) || "Enter a valid hex or rgb(a) color",
                          onChange: (e) => {
                            setThemeName(e.target.value);
                            if (e.target.value.length > 0) {
                              setThemeError(false);
                            } else {
                              setThemeError(true);
                            }
                          },
                        })}
                      />
                      <div
                        id="colored"
                        className="position-absolute end-0 me-2 border rounded"
                        style={{
                          background: themeName ?? "white",
                          width: "25px",
                          height: "25px",
                          cursor: "pointer",
                        }}
                        onClick={() => primaryColorPickerRef.current.click()}
                      ></div>
                    </div>

                    {themeError && (
                      <p role="alert" className="validation-error-msg">
                        Please add primary brand theme color!
                      </p>
                    )}
                  </div>
                  <div className="form-group mt-3 w-50 position-relative align-items-center">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Secondary Brand Theme Color<FieldBadge source={fieldSources.brandThemeSecondaryColor} />{" "}
                                         </label>

                    <input
                      type="color"
                      ref={secondaryColorPickerRef}
                      className="color-input w-12 h-10 hidden border rounded-md"
                      value={toHexColor(secondaryTheme)}
                      onChange={(e) => setSecondaryTheme(e.target.value)}
                    />

                    <div className="d-flex align-items-center w-100 position-relative">
                      <input
                        type="text"
                        className="form-control pe-5"
                        placeholder="#0ea5e9 or rgba(14,165,233,1)"
                        value={secondaryTheme}
                        {...register("secondaryTheme", {
                          // Optional: enrichment fills secondary color.
                          maxLength: 30,
                          validate: (v) => !v || isValidColor(v) || "Enter a valid hex or rgb(a) color",
                          onChange: (e) => {
                            setSecondaryTheme(e.target.value);
                          },
                        })}
                      />
                      <div
                        id="colored"
                        className="position-absolute end-0 me-2 border rounded"
                        style={{
                          background: secondaryTheme ?? "white",
                          width: "25px",
                          height: "25px",
                          cursor: "pointer",
                        }}
                        onClick={() => secondaryColorPickerRef.current.click()}
                      ></div>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-between gap-2">
                  <div className="form-group mt-3 w-50">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Call to Action Type                    </label>
                    <Select
                      options={actionTypeOptions}
                      value={actionTypeOptions.find(x => x.value === actionType)}
                      isMulti={false}
                      onChange={(options) => {setActionType(options.value); setActionTypeError(false);}}
                    />
                    {actionTypeError && (
                      <p role="alert" className="validation-error-msg">
                        Please select an action type
                      </p>
                    )}
                  </div>
                  <div className="form-group mt-3 w-50">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Call to Action URL                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={actionUrl}
                      placeholder="Call to Action URL"
                      {...register("actionUrl", {
                        // Optional: enrichment fills the action URL.
                        maxLength: 20,
                        onChange: (e) => {
                          setActionUrl(e.target.value);
                          if (e.target.value.length > 0) {
                            setActionUrlError(false);
                          } else {
                            setActionUrlError(true);
                          }
                        },
                      })}
                    />
                    {actionUrlError && (
                      <p role="alert" className="validation-error-msg">
                        Please add the URL
                      </p>
                    )}
                  </div>
                </div>
                <div className="form-check form-switch ml-1 mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="flexSwitchCheckChecked"
                    value={isSupervisorToggle}
                    onChange={(e) => setIsSupervisorToggle(e.target.checked)}
                    checked={isSupervisorToggle}
                  />
                  <label
                    className="form-check-label ml-2 f-14 inter text-black font-weight-500"
                    htmlFor="flexSwitchCheckChecked"
                  >
                    Enable Main Supervisor
                  </label>
                </div>
                <div className="form-group-div mt-3">
                  <label className="f-14 inter text-black font-weight-500">
                    Main Supervisor
                  </label>
                  <Select
                    isDisabled={!isSupervisorToggle}
                    value={supervisor}
                    options={supervisorsOptions}
                    onChange={(e) => setSupervisor(e)}
                  />
                </div>
                <div className="form-check form-switch ml-1 mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="enableListOfSupervisors"
                    value={isSupervisorsToggle}
                    onChange={(e) => setIsSupervisorsToggle(e.target.checked)}
                    checked={isSupervisorsToggle}
                  />
                  <label
                    className="form-check-label ml-2 f-14 inter text-black font-weight-500"
                    htmlFor="enableListOfSupervisors"
                  >
                    Enable List of Supervisors
                  </label>
                </div>
                <div className="form-group-div mt-3">
                  <label className="f-14 inter text-black font-weight-500">
                    List of Supervisors
                  </label>
                  <Select
                    isDisabled={!isSupervisorsToggle}
                    options={supervisorsOptions.filter(
                      (x) => x.value !== supervisor?.value ?? ""
                    )}
                    isMulti
                    value={supervisors}
                    onChange={(options) => setSupervisors(options)}
                  />
                </div>

                <div className="form-check form-switch ml-1 mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="enableSupervisorWorkflowEmails"
                    value={notifySupervisorOnWorkflows}
                    onChange={(e) => setNotifySupervisorOnWorkflows(e.target.checked)}
                    checked={notifySupervisorOnWorkflows}
                  />
                  <label
                    className="form-check-label ml-2 f-14 inter text-black font-weight-500"
                    htmlFor="enableSupervisorWorkflowEmails"
                  >
                    Email supervisor workflow updates
                  </label>
                  <p className="f-12 text-muted mb-0 ml-4 mt-1">
                    Info-only emails about scheduled / published / failed posts,
                    engagement pulls, brand enrichment, and campaigns. Sent to the
                    main supervisor selected above. Skipped if no supervisor is set.
                    Separate from the approval flow.
                  </p>
                </div>

                <div className="form-check form-switch ml-1 mt-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="enableAutoPost"
                    value={isAuto}
                    onChange={(e) => setIsAuto(e.target.checked)}
                    checked={isAuto}
                  />
                  <label
                    className="form-check-label ml-2 f-14 inter text-black font-weight-500"
                    htmlFor="enableAutoPost"
                  >
                    Auto Post
                  </label>
                </div>

                {isAuto && (
                  <div className="form-group-div mt-3">
                    <label className="f-14 inter text-black font-weight-500">
                      Select Time
                    </label>
                    <input
                      value={postingTime}
                      className=""
                      type="time"
                      id="time"
                      name="time"
                      onChange={(e) => setPostingTime(e.target.value)}
                    />
                  </div>
                )}

                <div className="form-group-div mt-3">
                  <label className="f-14 inter text-black font-weight-500">
                    Select Language
                  </label>
                  <Select
                    options={languageOptions}
                    value={languageOptions.find(
                      (option) => option.value === language
                    )}
                    onChange={(e) => setLanguage(e.value)}
                  />
                </div>

                <div className="d-flex justify-content-between gap-2">
                  <div className="form-group mt-3 w-50">
                    <label
                      htmlFor=""
                      className="f-14 inter text-black font-weight-500"
                    >
                      Brand Posting Cadence{" "}
                                         </label>
                    <div className="form-radio">
                      <input
                        type="radio"
                        id="everyday"
                        name="brandPostingCadence"
                        value="everyday"
                        checked={brandPostingCadence === "everyday"}
                        onChange={(e) => {
                          setBrandPostingCadence(e.target.value);
                          if (e.target.value.length > 0) {
                            setCadenceError(false);
                          } else {
                            setCadenceError(true);
                          }
                        }}
                      />
                      <label htmlFor="everyday">Everyday</label> {" "}
                      <input
                        type="radio"
                        id="week"
                        name="brandPostingCadence"
                        value="week"
                        checked={brandPostingCadence === "week"}
                        onChange={(e) => {
                          setBrandPostingCadence(e.target.value);
                          if (e.target.value.length > 0) {
                            setCadenceError(false);
                          } else {
                            setCadenceError(true);
                          }
                        }}
                      />
                      <label htmlFor="week">1 in week</label>{" "}
                      <input
                        type="radio"
                        id="month"
                        name="brandPostingCadence"
                        value="month"
                        checked={brandPostingCadence === "month"}
                        onChange={(e) => {
                          setBrandPostingCadence(e.target.value);
                          if (e.target.value.length > 0) {
                            setCadenceError(false);
                          } else {
                            setCadenceError(true);
                          }
                        }}
                      />
                      <label htmlFor="month">1 in month</label>
                    </div>
                  </div>
                </div>
                <div className="form-group mt-3">
                  <label
                    htmlFor=""
                    className="f-14 inter text-black font-weight-500"
                  >
                    Brand Posting Instructions<FieldBadge source={fieldSources.brandPostingInstructions} />
                  </label>
                  <textarea
                    onChange={(e) =>
                      setBrandPostingInstructions(e.target.value)
                    }
                    type="text"
                    value={brandPostingInstructions}
                    className="form-control"
                    placeholder="Brand Posting Instructions"
                  ></textarea>
                </div>
                <div className="form-group mt-3">
                  <label htmlFor="" className="f-14 inter text-black font-weight-500">
                    Brand logo / watermark URL<FieldBadge source={fieldSources.logoUrl} />
                  </label>
                  <div className="d-flex align-items-center justify-content-between">
                    <input
                      onChange={(e) => setLogoUrl(e.target.value)}
                      type="text"
                      value={logoUrl}
                      className="form-control"
                      placeholder="https://yourbrand.com/logo.png — stamped on generated post images"
                    />
                    <LogoUrlPreview url={logoUrl} alt="brand logo / watermark preview" testId="logo-url-preview" />
                  </div>
                </div>
                <div className="form-group mt-3">
                  <label htmlFor="" className="f-14 inter text-black font-weight-500">
                    Logo overlay URL<FieldBadge source={fieldSources.overlayLogoUrl} />
                  </label>
                  <div className="d-flex align-items-center justify-content-between">
                    <input
                      onChange={(e) => setOverlayLogoUrl(e.target.value)}
                      type="text"
                      value={overlayLogoUrl}
                      className="form-control"
                      placeholder="https://yourbrand.com/overlay-logo.png — corner badge overlaid on generated & perfectionist images"
                    />
                    <LogoUrlPreview url={overlayLogoUrl} alt="logo overlay preview" testId="overlay-logo-url-preview" />
                  </div>
                </div>
                {/* WHAT THE FALLBACK SCREENSHOT MUST LOOK LIKE.
                    When every image provider is down the pipeline screenshots a
                    keyword-matched stock search instead. That photo is chosen from the
                    SUBJECT alone, so it comes back on-topic and off-brand — the one image
                    in the pipeline nobody art-directed. Whatever look is typed here is
                    FORCED: welded onto every search the fallback runs, widened ones
                    included, and applied as a tone treatment to whatever comes back.

                    FREE TEXT, not a closed list — a brand whose look is "neon cyberpunk
                    night city" should not have to settle for the nearest preset. The
                    buttons below only fill the box in; the worker evaluates whatever ends
                    up in it (prigmar-worker services/imageFallbackThemes). */}
                <div className="form-group mt-3">
                  <label htmlFor="" className="f-14 inter text-black font-weight-500">
                    Fallback screenshot theme<FieldBadge source={fieldSources.imageFallbackTheme} />
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={imageFallbackTheme}
                    onChange={(e) => { const v = e.target.value; setImageFallbackTheme(v); }}
                    placeholder="Type a look — e.g. neon cyberpunk night city. Leave empty to match the subject only."
                    data-testid="image-fallback-theme"
                  />
                  <div className="d-flex flex-wrap mt-2" data-testid="image-fallback-theme-presets">
                    {IMAGE_FALLBACK_THEMES.map((t) => (
                      <button
                        key={t.id || 'none'}
                        type="button"
                        className={`btn btn-sm mr-2 mb-2 ${imageFallbackTheme === t.id ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => setImageFallbackTheme(t.id)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <small className="text-muted f-12">
                    Only used when every image generator fails and the pipeline falls back to a
                    stock screenshot. Your words become the search and, where they describe a
                    look (dark, warm, muted, black &amp; white…), the treatment on the photo —
                    so the fallback still looks like this brand. Keep it short: every word is
                    required in the search, and a whole sentence finds nothing.
                  </small>
                </div>
                <div className="form-group mt-3">
                  <label
                    htmlFor=""
                    className="f-14 inter text-black font-weight-500"
                  >
                    Global Brand Message For Audience<FieldBadge source={fieldSources.globalBrandMessageForAudience} />
                  </label>
                  <textarea
                    onChange={(e) => setGlobalMessage(e.target.value)}
                    value={globalMessage}
                    type="text"
                    className="form-control"
                    placeholder="Global Brand Message For Audience"
                  ></textarea>
                </div>

                {/* Settings — every auto/autopilot flag (merged in from the
                    former standalone Step 05, which has been removed). The
                    brand-wide auto-publish master is the "Auto Post" toggle
                    above; the per-feature switches inherit from it. */}
                <div className="form-group mt-4">
                  <p className="inter f-14 font-weight-bold mb-1">Settings</p>
                  <p className="inter f-12 font-weight-normal text-muted">
                    Control which automated flows run for this brand. Each switch
                    re-gates its pipeline as soon as you save — when off, work is
                    still generated but parked for approval instead of going live.
                  </p>

                  <div className="form-check form-switch ml-1 mt-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="autoGenerate"
                      checked={autoGenerate}
                      onChange={(e) => setAutoGenerate(e.target.checked)}
                    />
                    <label
                      className="form-check-label ml-2 f-14 inter text-black font-weight-500"
                      htmlFor="autoGenerate"
                    >
                      Auto generate content
                    </label>
                    <p className="f-12 text-muted mb-0 ml-4 mt-1">
                      Produce posts / emails / ideas automatically. When off, the
                      content schedulers skip this brand entirely.
                    </p>
                  </div>

                  {/* Per-feature autopilot switches. Unset keys inherit from the
                      "Auto Post" master toggle above via resolveAutopilotChecked. */}
                  {AUTOPILOT_FEATURES.map((f) => (
                    <div className="form-check form-switch ml-1 mt-3" key={f.key}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`autopilot-${f.key}`}
                        checked={resolveAutopilotChecked(autopilotFlags, f.key, {
                          autopost: isAuto,
                        })}
                        // Read e.target.checked synchronously (pooling-safe) —
                        // see makeAutopilotToggleHandler. Reading it inside the
                        // setState updater crashed the Auto enrich toggle with
                        // "Cannot read properties of null (reading 'checked')".
                        onChange={makeAutopilotToggleHandler(setAutopilotFlags, f.key)}
                      />
                      <label
                        className="form-check-label ml-2 f-14 inter text-black font-weight-500"
                        htmlFor={`autopilot-${f.key}`}
                      >
                        {f.label}
                      </label>
                      <p className="f-12 text-muted mb-0 ml-4 mt-1">{f.hint}</p>
                    </div>
                  ))}

                  {/* Auto newsletter + cadence */}
                  <div className="form-check form-switch ml-1 mt-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="autoNewsletter"
                      checked={autoNewsletter}
                      onChange={(e) => setAutoNewsletter(e.target.checked)}
                    />
                    <label
                      className="form-check-label ml-2 f-14 inter text-black font-weight-500"
                      htmlFor="autoNewsletter"
                    >
                      Auto newsletter
                    </label>
                    <p className="f-12 text-muted mb-0 ml-4 mt-1">
                      Self-provision the brand's newsletter (list, template, signup
                      widget) and send it on the cadence below.
                    </p>
                  </div>
                  {autoNewsletter && (
                    <div className="form-group-div mt-3">
                      <label className="f-14 inter text-black font-weight-500">
                        Newsletter frequency
                      </label>
                      <select
                        className="form-control"
                        value={autoNewsletterFrequency}
                        onChange={(e) => setAutoNewsletterFrequency(e.target.value)}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-5">
                <button
                  type="button"
                  className="header-btn text-bid border-bid"
                  onClick={() => goToStep(STEP.STEP_02)}
                >
                  <span className="mr-3 transform-180">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="12"
                      viewBox="0 0 8 12"
                      fill="none"
                    >
                      <path
                        d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                        fill="#09121F"
                      />
                    </svg>
                  </span>
                  Back
                </button>
                <button
                  type="button"
                  className="header-btn text-white bg-bid border-bid mr-0"
                  onClick={(e) => {
                    // Still no empty-field gate — enrichment fills empties and
                    // the setup-needed notification + email handles what truly
                    // blocks posting. goToStep stops only a typed value nothing
                    // downstream can use: a colour that isn't a colour, a
                    // call-to-action URL that isn't a URL.
                    goToStep(STEP.STEP_04);
                  }}
                >
                  Next
                  <span className="ml-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="12"
                      viewBox="0 0 8 12"
                      fill="none"
                    >
                      <path
                        d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                        fill="white"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            </>
          ) : null}

          {/* Step 03 only for Revit*/}
          {selectedSteps === STEP.STEP_04 ? (
            <>
              <div className="form-group mt-3">
                <div className="form-group-div mt-3">
                  <label className="f-14 inter text-black font-weight-500">
                    Posting Content Type
                  </label>
                  <Select
                    options={contentTypeOptions}
                    value={contentTypeOptions.find(
                      (option) => option.value === contentType
                    )}
                    onChange={(e) => setContentType(e.value)}
                  />
                </div>
                <div className="form-group mt-3 w-50">
                  <label className="f-14 inter text-black font-weight-500">
                    Image Generation Model Training{" "}
                                     </label>
                  <div className="radio-margin">
                    <input
                      type="radio"
                      id="person"
                      name="modelType"
                      value="Person"
                      checked={modelType === "Person"}
                      onChange={(e) => setModelType(e.target.value)}
                    />
                    <label htmlFor="person">Person</label> 
                    <input
                      type="radio"
                      id="product"
                      name="modelType"
                      value="Product"
                      checked={modelType === "Product"}
                      onChange={(e) => setModelType(e.target.value)}
                    />
                    <label htmlFor="product">Product</label> 
                    <input
                      type="radio"
                      id="concept"
                      name="modelType"
                      value="Concept"
                      checked={modelType === "Concept"}
                      onChange={(e) => setModelType(e.target.value)}
                    />
                    <label htmlFor="concept">Concept</label>
                  </div>
                </div>
                {contentType !== 'text' && <>
                  <label
                    htmlFor=""
                    className="f-14 inter text-black font-weight-500"
                  >
                    Upload at least 10 images for AI Model (High Resolution)
                  </label>
                  <div className="d-flex align-items-center justify-content-between">
                    <div
                      className="profile-upload-box cursor-pointer w-fill d-flex align-items-center my-0 justify-content-between"
                      onClick={() => {
                        if (contentType === "text") {
                          alert(
                            "You have selected 'Text' content type. Please select 'Image' or 'Text + Image' to upload images."
                          );
                          return;
                        }
                        modelImgsInputRef.current.click();
                      }}
                    >
                      <div className="text-left">
                        <p className="font-weight-bold text-black f-14">
                          Click to upload{" "}
                          <span className="font-weight-light">
                            or drag and drop
                          </span>
                        </p>
                        <p className="f-12">
                          SVG, PNG, JPG or GIF (High Resolution)
                        </p>
                      </div>
                      <img
                        src={UploadIcon}
                        className="upload-icon"
                        alt="Upload"
                      />
                    </div>
                  </div>
                </>}

                <div className="uploaded-images-container">
                  {modelImages?.map((image, index) => (
                    <div key={index} className="image-container">
                      <img
                        src={
                          image?.isUploaded
                            ? image.url
                            : window.URL.createObjectURL(image)
                        }
                        alt={`Uploaded ${index + 1}`}
                        className="uploaded-icon"
                      />
                      <button
                        type="button"
                        className="remove-button"
                        onClick={(e) => {e.stopPropagation(); handleRemoveImage(index)}}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <input
                  ref={modelImgsInputRef}
                  onChange={handleModelImages}
                  disabled={contentType === "text"}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                />
              </div>
              <div className="d-flex align-items-center justify-content-between mt-5">
                <button
                  type="button"
                  className="header-btn text-bid border-bid"
                  onClick={() => goToStep(STEP.STEP_03)}
                >
                  <span className="mr-3 transform-180">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="8"
                      height="12"
                      viewBox="0 0 8 12"
                      fill="none"
                    >
                      <path
                        d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                        fill="#09121F"
                      />
                    </svg>
                  </span>
                  Back
                </button>
                <button
                  type="submit"
                  className="header-btn text-white bg-bid border-bid mr-0 position-relative"
                  // NOT the final step any more — Promotion follows. No
                  // image-upload gate: enrichment auto-fills modelImages from
                  // the brand's top-engagement posts and the user can upload
                  // their own later, so empty modelImages is valid. Submitting
                  // saves the brand (all Settings now live in Step 03) and then
                  // advances to Promotion, which is why this says "continue"
                  // rather than "Next" — the save has genuinely happened by the
                  // time the next step renders, and hiding that would be worse.
                >
                  Save &amp; continue
                  {imageUploadLoading ? (
                    <i className="fa fa-spinner small-spinner ml-3" />
                  ) : exportsettingsloading ? (
                    <i className="fa fa-spinner small-spinner ml-3" />
                  ) : (
                    <span className="ml-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="8"
                        height="12"
                        viewBox="0 0 8 12"
                        fill="none"
                      >
                        <path
                          d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            </>
          ) : null}
        </form>
        {selectedSteps === STEP.STEP_05 ? (
          <div className="promotion-step" style={{ padding: "8px 4px 0" }}>
            <h4>Promotion</h4>
            <PromotionEntries
              features={promotion.features}
              ctaText={promotion.ctaText}
              ctaUrl={promotion.ctaUrl}
              onChange={(features) => setPromotion((p) => ({ ...p, features }))}
              onMeta={(patch) => setPromotion((p) => ({ ...p, ...patch }))}
            />
            {/* Same footer shape as every other step — Back bottom-left,
                the forward action bottom-right. This step had neither: its
                buttons sat together on the left and there was no way back to
                Content Generation at all, so the last step of an EDIT was a
                dead end unless you closed the modal. */}
            <div className="d-flex align-items-center justify-content-between mt-5">
              <button
                type="button"
                className="header-btn text-bid border-bid"
                onClick={() => goToStep(previousStep(STEP.STEP_05))}
              >
                <span className="mr-3 transform-180">
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                    <path
                      d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                      fill="#09121F"
                    />
                  </svg>
                </span>
                Back
              </button>
              <div className="d-flex align-items-center">
                {/* Kept — promotion is genuinely optional, and removing the
                    way past it would make the wizard harder to finish. */}
                <button
                  type="button"
                  className="header-btn text-bid border-bid"
                  onClick={() => setSelectedSteps(STEP.STEP_06)}
                >
                  Skip for now
                </button>
                <button
                  type="button"
                  className="header-btn text-white bg-bid border-bid mr-0 ml-3"
                  onClick={savePromotion}
                  disabled={savingPromotion}
                >
                  {savingPromotion ? "Saving…" : "Save & continue"}
                  {!savingPromotion && (
                    <span className="ml-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                        <path
                          d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {selectedSteps === STEP.STEP_06 ? (
          <>
            <div className="successfully-brand-created-div">
              <div>
                <img src={SuccessIMG} alt="" />
                <p className="f-14 inter font-weight-500 mb-0">Well Done!</p>
                {/* Edit mode reaches this screen now that saving advances to
                    Promotion instead of closing the modal, so "created" is no
                    longer true for every visitor. */}
                <p className="f-14 inter font-weight-light">
                  {props.brand ? "Brand updated Successfully" : "Brand created Successfully"}
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-end mt-5">
              <button
                className="header-btn text-white bg-bid border-bid mr-0"
                onClick={() => toggle()}
              >
                All Brands
                <span className="ml-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="12"
                    viewBox="0 0 8 12"
                    fill="none"
                  >
                    <path
                      d="M4.97633 6.00058L0.851562 1.87577L2.03007 0.697266L7.33341 6.00058L2.03007 11.3038L0.851562 10.1253L4.97633 6.00058Z"
                      fill="white"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </>
        ) : null}
      </MDBModalBody>
    </MDBModal>
  );
};

export default CreateModal;
